import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import "./themeupdate.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import { s3PublicUrl } from "../../../utils/s3PublicUrl";
import * as XLSX from "xlsx";
import { THEME0_STAGES_SKELETON } from "../../theme0StagesSkeleton";
import { computePuzzleGrid } from "../../../utils/puzzleGrid";

const DAY_COUNT = 10;
const PUZZLE_PARTS_MIN = 2;
const PUZZLE_PARTS_MAX = 40;
const DAY1_WORD_MAX = 50;
const DAY1_WORD_CHAR_MAX = 50;
const DAY1_WORD_ALLOWED_HINT = "A–Z, spaces, and hyphens (-)";
const DAY1_SAMPLE_ROWS = [
  ["Word", "Type", "Did You Know"],
  ["HELLO", "good", ""],
  ["TEAM WORK", "good", ""],
  ["WELL-BEING", "bad", ""],
];
const DAY3_TOPIC_MAX = 50;
const DAY3_TOPIC_CHAR_MAX = 100;
const DAY3_ANSWER_CHAR_MAX = 100;
const DAY3_ANSWERS_PER_TOPIC_MAX = 100;
const DAY3_SAMPLE_ROWS = [
  ["Word", "Acceptable Answers (| separated)", "Not Acceptable Answers (| separated)"],
  ["Science Fiction", "Dune|Star Wars|The Matrix", "Titanic|The Notebook"],
  ["Action Movies", "Die Hard|Mad Max|John Wick", "Frozen|Toy Story"],
  ["Board Games", "Chess|Monopoly|Catan", "Poker|Solitaire"],
  ["Classic Literature and Poetry", "Hamlet|The Odyssey|Pride and Prejudice", "Twilight"],
];
const LOGO_DIMENSIONS = { width: 400, height: 145 };
const PUZZLE_IMAGE_DIMENSIONS = { width: 1920, height: 1080 };
const PUZZLE_IMAGE_MAX_BYTES = 5 * 1024 * 1024;

// Per-day config for question types, character limits, and max questions
const DAY_CONFIG = {
  1: { type: "super_power_squad", maxQ: 0, questionLabel: null, questionMax: 0, answerMax: null, answerLabel: null, hasOptions: false },
  2: { type: "comic_strip_chaos", maxQ: 0, questionLabel: null, questionMax: 0, answerMax: null, answerLabel: null, hasOptions: false },
  3: { type: "category_rush", maxQ: DAY3_TOPIC_MAX, questionLabel: "Word", questionMax: DAY3_TOPIC_CHAR_MAX, answerMax: null, answerLabel: null, hasOptions: false },
  4: { type: "word_chain", maxQ: 0, questionLabel: null, questionMax: 0, answerMax: null, answerLabel: null, hasOptions: false },
  5: { type: "wordle",  maxQ: DAY1_WORD_MAX,  questionLabel: "Word",    questionMax: DAY1_WORD_CHAR_MAX,   answerMax: null, answerLabel: null,  hasOptions: false },
  6: { type: "team_anthem", maxQ: 0, questionLabel: null, questionMax: 0, answerMax: null, answerLabel: null, hasOptions: false },
  7: { type: "super_squad", maxQ: 0, questionLabel: null, questionMax: 0, answerMax: null, answerLabel: null, hasOptions: false },
  8: { type: "wild_tales", maxQ: 0, questionLabel: null, questionMax: 0, answerMax: null, answerLabel: null, hasOptions: false },
  9: { type: "letter_loot", maxQ: 0, questionLabel: null, questionMax: 0, answerMax: null, answerLabel: null, hasOptions: false },
  10: { type: "native_places", maxQ: 0, questionLabel: null, questionMax: 0, answerMax: null, answerLabel: null, hasOptions: false },
};

// Display name of the game mapped to each stage number (matches DAY_CONFIG / stageRoutes).
const STAGE_GAME_NAMES = {
  1: "Super Power Squad",
  2: "Comic Strip Chaos",
  3: "Category Rush",
  4: "Word Chain",
  5: "Wordle",
  6: "Team Anthem",
  7: "Super Squad",
  8: "Wild Tales",
  9: "Letter Loot",
  10: "Native Places",
};

const DID_YOU_KNOW_MAX = 250;
const STREAK_MESSAGE_MAX = 150;
const TITLE_MAX = 80;
const DESCRIPTION_MAX = 500;
const DAY_DESCRIPTION_MAX = {
  2: 1000,
  4: 100,
  6: 500,
  8: 100,
  9: 100,
  10: 150,
};
const STAGE_DESCRIPTION_EXAMPLES = {
  1: 'Darshan → Captain Deadline · Priya → Quantum Queen · Team Name → The Productivity Protectors · Slogan → "United We Zoom."',
  2: 'Respect: Person A → I should probably listen first… shocking, I know. · Person B → Finally, last time your "quick idea" turned into a 20-minute monologue.',
  4: 'Apple → Elephant → Tiger → Rabbit → Tomato → Orange',
  6: 'Your team is about to enter the grand finale. Every voice matters. Write a short team anthem — rap, chant, or poem — that brings everyone together.',
  7: 'Darshan → Captain Deadline · Priya → Quantum Queen · Team Name → The Productivity Protectors · Slogan → "United We Zoom."',
  9: 'Harsh, Muzammil, Alex → HA MU AL → HAM, ALUM, BALM, HUM',
  10: "Mumbai → Food: Vada Pav · Landmark: Gateway of India · Fun Fact: India's largest film industry · Must Try: Marine Drive at sunset",
};
const STAGE_DEFAULT_TIMER = '03:00';
const STAGE_DEFAULT_TIMER_PARTS = { min: 3, sec: 0 };
const STAGE_TIMER_MIN_MINUTES = 2;
const STAGE_TIMER_MAX_MINUTES = 20;
const STAGE_TIMER_MIN_SECONDS = STAGE_TIMER_MIN_MINUTES * 60;
const STAGE_TIMER_MAX_SECONDS = STAGE_TIMER_MAX_MINUTES * 60;

function getStageTimerTotalSeconds(raw) {
  const { min, sec } = parseStageTimerParts(raw);
  return min * 60 + sec;
}

function validateStageTimerValue(timer, dayNumber) {
  const total = getStageTimerTotalSeconds(timer);
  const label = dayNumber != null ? `Stage ${dayNumber}` : "Stage";
  if (total < STAGE_TIMER_MIN_SECONDS) {
    Swal.fire({
      icon: "warning",
      title: "Timer too short",
      text: `${label} countdown must be at least ${STAGE_TIMER_MIN_MINUTES} minutes.`,
    });
    return false;
  }
  if (total > STAGE_TIMER_MAX_SECONDS) {
    Swal.fire({
      icon: "warning",
      title: "Timer too long",
      text: `${label} countdown cannot exceed ${STAGE_TIMER_MAX_MINUTES} minutes.`,
    });
    return false;
  }
  return true;
}

function parseStageTimerParts(raw) {
  const fallback = { ...STAGE_DEFAULT_TIMER_PARTS };
  const s = String(raw ?? '').trim();
  if (!s) return fallback;
  if (s.includes(':')) {
    const [minPart, secPart] = s.split(':');
    const min = Math.max(0, Math.min(STAGE_TIMER_MAX_MINUTES, Math.floor(Number(minPart)) || 0));
    const sec = Math.max(0, Math.min(59, Math.floor(Number(secPart)) || 0));
    return { min, sec };
  }
  const total = Math.floor(Number(s));
  if (Number.isFinite(total) && total > 0) {
    return {
      min: Math.max(0, Math.min(STAGE_TIMER_MAX_MINUTES, Math.floor(total / 60))),
      sec: Math.max(0, Math.min(59, total % 60)),
    };
  }
  return fallback;
}

function formatStageTimer(min, sec) {
  const m = Math.max(0, Math.min(STAGE_TIMER_MAX_MINUTES, Math.floor(Number(min)) || 0));
  const s = Math.max(0, Math.min(59, Math.floor(Number(sec)) || 0));
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function filterTimerDigits(raw, max) {
  const digits = String(raw ?? "").replace(/\D/g, "");
  if (!digits) return "";
  return String(Math.min(max, Number(digits)));
}

function blockInvalidTimerKeys(e) {
  if (["-", "+", "e", "E", ".", ","].includes(e.key)) {
    e.preventDefault();
  }
}

function StageTimerInput({ stage, updateStage, style }) {
  const parsed = parseStageTimerParts(stage.timer ?? STAGE_DEFAULT_TIMER);
  const [minDraft, setMinDraft] = useState(String(parsed.min));
  const [secDraft, setSecDraft] = useState(String(parsed.sec));

  useEffect(() => {
    const { min, sec } = parseStageTimerParts(stage.timer ?? STAGE_DEFAULT_TIMER);
    setMinDraft(String(min));
    setSecDraft(String(sec));
  }, [stage.timer]);

  const resolveParts = (minStr, secStr) => ({
    min: minStr === "" ? 0 : Math.max(0, Math.min(STAGE_TIMER_MAX_MINUTES, Math.floor(Number(minStr)) || 0)),
    sec: secStr === "" ? 0 : Math.max(0, Math.min(59, Math.floor(Number(secStr)) || 0)),
  });

  const applyTimerDraft = () => {
    const { min, sec } = resolveParts(minDraft, secDraft);
    const total = min * 60 + sec;

    if (total < STAGE_TIMER_MIN_SECONDS) {
      Swal.fire({
        icon: "warning",
        title: "Timer too short",
        text: `Countdown must be at least ${STAGE_TIMER_MIN_MINUTES} minutes. Timer cleared.`,
      });
      updateStage({ timer: STAGE_DEFAULT_TIMER });
      const reset = parseStageTimerParts(STAGE_DEFAULT_TIMER);
      setMinDraft(String(reset.min));
      setSecDraft(String(reset.sec));
      return;
    }

    if (total > STAGE_TIMER_MAX_SECONDS) {
      Swal.fire({
        icon: "warning",
        title: "Timer too long",
        text: `Countdown cannot exceed ${STAGE_TIMER_MAX_MINUTES} minutes. Timer cleared.`,
      });
      updateStage({ timer: STAGE_DEFAULT_TIMER });
      const reset = parseStageTimerParts(STAGE_DEFAULT_TIMER);
      setMinDraft(String(reset.min));
      setSecDraft(String(reset.sec));
      return;
    }

    updateStage({ timer: formatStageTimer(min, sec) });
    setMinDraft(String(min));
    setSecDraft(String(sec));
  };

  const handleMinChange = (e) => {
    const next = filterTimerDigits(e.target.value, STAGE_TIMER_MAX_MINUTES);
    setMinDraft(next);
  };

  const handleSecChange = (e) => {
    const next = filterTimerDigits(e.target.value, 59);
    setSecDraft(next);
  };

  const handleMinBlur = (e) => {
    if (e.relatedTarget?.getAttribute?.("aria-label") === "Timer seconds") return;
    applyTimerDraft();
  };

  const handleSecBlur = (e) => {
    if (e.relatedTarget?.getAttribute?.("aria-label") === "Timer minutes") return;
    applyTimerDraft();
  };

  return (
    <div className="themeupdate-total-questions" style={{ marginBottom: 18, ...style }}>
      <label className="themeupdate-label">Timer (min - sec)</label>
      <div className="themeupdate-timer-row">
        <div className="themeupdate-timer-field">
          <span className="themeupdate-timer-field-label">Min</span>
          <input
            type="text"
            inputMode="numeric"
            className="themeupdate-input themeupdate-timer-input"
            value={minDraft}
            onChange={handleMinChange}
            onBlur={handleMinBlur}
            onKeyDown={blockInvalidTimerKeys}
            aria-label="Timer minutes"
          />
        </div>
        <span className="themeupdate-timer-separator" aria-hidden>
          :
        </span>
        <div className="themeupdate-timer-field">
          <span className="themeupdate-timer-field-label">Sec</span>
          <input
            type="text"
            inputMode="numeric"
            className="themeupdate-input themeupdate-timer-input"
            value={secDraft}
            onChange={handleSecChange}
            onBlur={handleSecBlur}
            onKeyDown={blockInvalidTimerKeys}
            aria-label="Timer seconds"
          />
        </div>
      </div>
      <div className="themeupdate-help" style={{ marginTop: 6 }}>
        <span>
          Countdown shown to players. Between {STAGE_TIMER_MIN_MINUTES} and {STAGE_TIMER_MAX_MINUTES}{" "}
          minutes (default 03:00).
        </span>
      </div>
    </div>
  );
}
/** @deprecated Use TITLE_MAX */
const RULES_TITLE_MAX = TITLE_MAX;

function formatDescriptionValue(value, lineSeparator = " ") {
  if (value == null) {
    return "";
  }
  if (typeof value === "string") {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => formatDescriptionValue(item, lineSeparator)).join(lineSeparator);
  }
  if (typeof value === "object") {
    return Object.entries(value)
      .map(([key, item]) => {
        const formattedItem = formatDescriptionValue(item, lineSeparator);
        return formattedItem ? `${key}: ${formattedItem}` : key;
      })
      .join(lineSeparator);
  }
  return String(value);
}

function descriptionToTextarea(value) {
  return formatDescriptionValue(value, " ")
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/\r?\n/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function descriptionFromTextarea(value) {
  return String(value ?? "")
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/\r?\n/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function descriptionToTextareaMultiline(value) {
  return formatDescriptionValue(value, "\n").replace(/<br\s*\/?>/gi, "\n");
}

function descriptionFromTextareaMultiline(value) {
  return String(value ?? "")
    .replace(/\r\n/g, "\n")
    .split("\n")
    .join("<br/>");
}

function getDayDescriptionMax(day) {
  return DAY_DESCRIPTION_MAX[day] ?? DESCRIPTION_MAX;
}

function buildCrosswordLayoutWords(questions) {
  const list = Array.isArray(questions) ? questions : [];
  return [...list]
    .filter((q) => String(q?.answer ?? '').trim().length >= 2)
    .sort(
      (a, b) =>
        String(b.answer ?? '').replace(/\s/g, '').length -
        String(a.answer ?? '').replace(/\s/g, '').length
    )
    .map((q) => ({
      answer: String(q.answer).replace(/\s/g, '').toUpperCase(),
      question: String(q.question ?? ''),
      did_you_know: String(q.did_you_know ?? ''),
    }));
}

function formatWordleWordCore(raw) {
  return String(raw ?? "")
    .replace(/[^a-zA-Z\s-]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();
}

function cleanWordleWord(raw) {
  return formatWordleWordCore(raw).slice(0, DAY1_WORD_CHAR_MAX);
}

function normalizeDay1WordType(raw) {
  const val = String(raw ?? "").trim().toLowerCase();
  return val === "bad" ? "bad" : "good";
}

function normalizeWordleRows(rawRows) {
  const out = [];
  const seen = new Set();
  const tooLongWords = [];
  for (const row of rawRows || []) {
    const rawWord = row?.word ?? row?.[0] ?? row;
    const rawLabel = String(rawWord ?? "").trim().toLowerCase();
    if (rawLabel === "word" || rawLabel === "words") continue;

    const cleaned = formatWordleWordCore(rawWord);
    if (!cleaned) continue;
    if (cleaned.length > DAY1_WORD_CHAR_MAX) {
      tooLongWords.push(cleaned);
      continue;
    }
    if (seen.has(cleaned)) continue;
    seen.add(cleaned);

    const rawTypeFromObj = row?.type ?? row?.kind ?? row?.word_type ?? row?.wordType ?? "";
    const rawSecond = row?.[1] ?? "";
    const rawThird = row?.[2] ?? "";
    const secondLooksLikeType = ["good", "bad"].includes(String(rawSecond ?? "").trim().toLowerCase());
    const type = normalizeDay1WordType(rawTypeFromObj || (secondLooksLikeType ? rawSecond : ""));
    const didYouKnowRaw = row?.did_you_know ?? row?.didYouKnow ?? (secondLooksLikeType ? rawThird : rawSecond) ?? "";

    out.push({
      word: cleaned,
      type,
      did_you_know: String(didYouKnowRaw ?? "").slice(0, DID_YOU_KNOW_MAX),
    });
    if (out.length >= DAY1_WORD_MAX) break;
  }
  return { rows: out, tooLongWords };
}

function downloadDay1SampleCsv() {
  const csv = DAY1_SAMPLE_ROWS.map((row) =>
    row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
  ).join("\r\n");
  const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "day1-words-sample.csv";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function normalizeDay1Questions(stage) {
  const fromQuestions = Array.isArray(stage?.questions) ? stage.questions : [];
  if (fromQuestions.length) {
    return fromQuestions.map((q) => ({
      word: cleanWordleWord(q?.word ?? q?.question ?? q?.answer ?? ""),
      type: normalizeDay1WordType(q?.type ?? q?.kind ?? q?.word_type ?? q?.wordType ?? ""),
      did_you_know: String(q?.did_you_know ?? "").slice(0, DID_YOU_KNOW_MAX),
    })).filter((q) => q.word);
  }
  const fromWords = Array.isArray(stage?.words) ? stage.words : [];
  return fromWords
    .map((w) => ({
      word: cleanWordleWord(w),
      type: "good",
      did_you_know: "",
    }))
    .filter((q) => q.word)
    .slice(0, DAY1_WORD_MAX);
}

function formatTopicLabelCore(raw) {
  return String(raw ?? "")
    .replace(/\s+/g, " ")
    .trim();
}

function cleanTopicLabel(raw) {
  return formatTopicLabelCore(raw).slice(0, DAY3_TOPIC_CHAR_MAX);
}

function cleanTopicAnswer(raw) {
  return formatTopicLabelCore(raw).slice(0, DAY3_ANSWER_CHAR_MAX);
}

function dedupeTopicAnswers(list) {
  const seen = new Set();
  const out = [];
  const source = Array.isArray(list) ? list : [];
  for (const raw of source) {
    const clean = cleanTopicAnswer(raw);
    if (!clean) continue;
    const key = clean.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(clean);
    if (out.length >= DAY3_ANSWERS_PER_TOPIC_MAX) break;
  }
  return out;
}

// Answers within a single CSV cell are separated by "|" or ";"
function splitTopicAnswerCell(raw) {
  return dedupeTopicAnswers(String(raw ?? "").split(/[|;]/));
}

function normalizeTopicRows(rawRows) {
  const out = [];
  const seen = new Set();
  const tooLongWords = [];
  let excessWordCount = 0;
  for (const row of rawRows || []) {
    const isArrayRow = Array.isArray(row);
    const rawWord = isArrayRow ? row[0] : row?.word ?? row;
    const rawLabel = String(rawWord ?? "").trim().toLowerCase();
    if (rawLabel === "word" || rawLabel === "words") continue;

    const cleaned = formatTopicLabelCore(rawWord);
    if (!cleaned) continue;
    if (cleaned.length > DAY3_TOPIC_CHAR_MAX) {
      tooLongWords.push(cleaned.length > 40 ? `${cleaned.slice(0, 40)}…` : cleaned);
      continue;
    }
    const key = cleaned.toLowerCase();
    if (seen.has(key)) continue;
    if (out.length >= DAY3_TOPIC_MAX) {
      excessWordCount += 1;
      continue;
    }
    seen.add(key);
    const acceptableRaw = isArrayRow ? row[1] : row?.answers;
    const rejectedRaw = isArrayRow ? row[2] : row?.rejected_answers ?? row?.bad_answers;
    out.push({
      word: cleaned,
      answers: splitTopicAnswerCell(acceptableRaw),
      rejected_answers: splitTopicAnswerCell(rejectedRaw),
      did_you_know: "",
    });
  }
  return { rows: out, tooLongWords, excessWordCount };
}

function downloadDay3SampleCsv() {
  const csv = DAY3_SAMPLE_ROWS.map((row) =>
    row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
  ).join("\r\n");
  const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "day3-words-sample.csv";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function normalizeDay3Questions(stage) {
  const fromQuestions = Array.isArray(stage?.questions) ? stage.questions : [];
  if (fromQuestions.length) {
    return fromQuestions
      .map((q) => ({
        word: cleanTopicLabel(q?.word ?? q?.question ?? q?.topic ?? ""),
        answers: dedupeTopicAnswers(q?.answers),
        rejected_answers: dedupeTopicAnswers(q?.rejected_answers ?? q?.bad_answers),
        did_you_know: String(q?.did_you_know ?? "").slice(0, DID_YOU_KNOW_MAX),
      }))
      .filter((q) => q.word)
      .slice(0, DAY3_TOPIC_MAX);
  }
  const fromTopics = Array.isArray(stage?.topics) ? stage.topics : [];
  if (fromTopics.length) {
    return fromTopics
      .map((t) => ({
        word: cleanTopicLabel(t),
        answers: [],
        rejected_answers: [],
        did_you_know: "",
      }))
      .filter((q) => q.word)
      .slice(0, DAY3_TOPIC_MAX);
  }
  const fromWords = Array.isArray(stage?.words) ? stage.words : [];
  return fromWords
    .map((w) => ({
      word: cleanTopicLabel(w),
      answers: [],
      rejected_answers: [],
      did_you_know: "",
    }))
    .filter((q) => q.word)
    .slice(0, DAY3_TOPIC_MAX);
}

function parseCsvWordRows(text) {
  const lines = String(text ?? "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  return lines.map((line) => {
    const parts = line.split(",").map((p) => p.trim().replace(/^"|"$/g, ""));
    return { word: parts[0], did_you_know: parts[1] ?? "" };
  });
}

// Robust CSV parser that returns an array of cell arrays and supports quoted
// fields (so commas inside quotes are preserved). Used for multi-column imports.
function parseCsvCellRows(text) {
  const rows = [];
  const lines = String(text ?? "").split(/\r?\n/);
  for (const line of lines) {
    if (!line.trim()) continue;
    const cells = [];
    let cur = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i += 1) {
      const ch = line[i];
      if (inQuotes) {
        if (ch === '"') {
          if (line[i + 1] === '"') {
            cur += '"';
            i += 1;
          } else {
            inQuotes = false;
          }
        } else {
          cur += ch;
        }
      } else if (ch === '"') {
        inQuotes = true;
      } else if (ch === ",") {
        cells.push(cur);
        cur = "";
      } else {
        cur += ch;
      }
    }
    cells.push(cur);
    rows.push(cells.map((c) => c.trim()));
  }
  return rows;
}

async function parseExcelWordRows(file) {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  const sheetName = wb.SheetNames[0];
  if (!sheetName) return [];
  const rows = XLSX.utils.sheet_to_json(wb.Sheets[sheetName], { header: 1, defval: "" });
  return rows.map((row) => ({
    word: Array.isArray(row) ? row[0] : "",
    did_you_know: Array.isArray(row) ? row[1] : "",
  }));
}

function parsePuzzleImgArray(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map((p) => String(p ?? "").trim()).filter(Boolean);
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed)
        ? parsed.map((p) => String(p ?? "").trim()).filter(Boolean)
        : [];
    } catch {
      return [];
    }
  }
  return [];
}

function clampPuzzleParts(value, fallback = 9) {
  const val = Math.floor(Number(value));
  if (!Number.isFinite(val)) {
    return Math.min(Math.max(fallback, PUZZLE_PARTS_MIN), PUZZLE_PARTS_MAX);
  }
  return Math.min(Math.max(val, PUZZLE_PARTS_MIN), PUZZLE_PARTS_MAX);
}

function resolvePuzzlePartsCount(themeData) {
  const fromArray = parsePuzzleImgArray(themeData?.img_array).length;
  const totalPuzzles = Math.floor(Number(themeData?.total_puzzles));
  const legacyParts = Math.floor(Number(themeData?.puzzle_parts));

  if (
    Number.isFinite(totalPuzzles) &&
    totalPuzzles >= PUZZLE_PARTS_MIN &&
    totalPuzzles <= PUZZLE_PARTS_MAX
  ) {
    return totalPuzzles;
  }

  if (fromArray >= PUZZLE_PARTS_MIN && fromArray <= PUZZLE_PARTS_MAX) {
    return fromArray;
  }

  if (
    Number.isFinite(legacyParts) &&
    legacyParts >= PUZZLE_PARTS_MIN &&
    legacyParts <= PUZZLE_PARTS_MAX
  ) {
    return legacyParts;
  }

  return 9;
}

function pickNonEmptyString(...values) {
  for (const v of values) {
    const s = String(v ?? "").trim();
    if (s) return s;
  }
  return "";
}

function pickDescriptionString(...values) {
  for (const v of values) {
    if (v == null) continue;
    if (typeof v === "string") {
      if (v.trim()) return v;
      continue;
    }
    if (typeof v === "object") {
      const formatted = formatDescriptionValue(v, "<br/>").trim();
      if (formatted) return formatted;
      continue;
    }
    const s = String(v).trim();
    if (s && s !== "[object Object]") return s;
  }
  return "";
}

function mergeStagesPayload(skeleton, parsed) {
  const sk = skeleton && typeof skeleton === "object" && !Array.isArray(skeleton) ? skeleton : {};
  const db = parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  const out = { ...sk };
  for (const [key, dbStage] of Object.entries(db)) {
    const skStage = sk[key];
    if (dbStage && typeof dbStage === "object" && !Array.isArray(dbStage)) {
      out[key] =
        skStage && typeof skStage === "object" && !Array.isArray(skStage)
          ? { ...skStage, ...dbStage }
          : dbStage;
    } else if (dbStage != null) {
      out[key] = dbStage;
    }
  }
  return out;
}

function parseStagesDataRaw(raw) {
  if (raw == null) return null;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (typeof raw === "object" && !Array.isArray(raw)) return raw;
  return null;
}

function isEmptyStage(st) {
  if (!st || typeof st !== "object") return true;
  const titleEmpty = !String(st.title ?? "").trim();
  const logoEmpty = !String(st.logo ?? "").trim();
  const descEmpty = !descriptionToTextarea(st.description).trim();
  const rulesEmpty =
    !Array.isArray(st.rules) || !st.rules.some((r) => String(r ?? "").trim());
  return titleEmpty && logoEmpty && descEmpty && rulesEmpty;
}

function getRawStage(raw, dayNum) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const underscored = raw[`stage_${dayNum}`];
  if (underscored && typeof underscored === "object" && !Array.isArray(underscored)) {
    return underscored;
  }
  const compact = raw[`stage${dayNum}`];
  if (compact && typeof compact === "object" && !Array.isArray(compact)) {
    return compact;
  }
  return null;
}

function normalizeStagesData(raw, skeletonRaw = THEME0_STAGES_SKELETON) {
  const parsed = parseStagesDataRaw(raw);
  const skeleton = parseStagesDataRaw(skeletonRaw);
  const merged = mergeStagesPayload(skeleton, parsed);
  const base = {};
  for (let i = 1; i <= DAY_COUNT; i++) {
    base[`stage_${i}`] = {
      type: DAY_CONFIG[i].type,
      max_questions: 0,
      rules: [""],
      questions: [],
      badge: "",
      streak: "",
      streak_message: "",
      title: "",
      question_image: "",
      did_you_know_image: "",
      bonus_streak_badge: "",
      bonus_streak_heading: "",
      logo: "",
      description: "",
      timer: STAGE_DEFAULT_TIMER,
    };
  }
  if (!merged || typeof merged !== "object") return base;
  const out = { ...base };
  for (let i = 1; i <= DAY_COUNT; i++) {
    const k = `stage_${i}`;
    const s = getRawStage(merged, i);
    if (!s) continue;
    out[k] = {
      ...out[k],
      ...s,
      type: DAY_CONFIG[i].type,
      max_questions:
        s.max_questions != null && String(s.max_questions).trim() !== ""
          ? Number(s.max_questions)
          : out[k].max_questions,
      rules:
        Array.isArray(s.rules) && s.rules.some((r) => String(r ?? "").trim())
          ? s.rules.map((x) => String(x ?? ""))
          : out[k].rules,
      questions:
        i === 5
          ? normalizeDay1Questions(s)
          : i === 3
            ? normalizeDay3Questions(s)
            : Array.isArray(s.questions)
              ? s.questions
              : [],
      badge: s.badge != null ? String(s.badge) : out[k].badge,
      streak:
        s.streak != null
          ? String(s.streak)
          : s.streakBadge != null
            ? String(s.streakBadge)
            : out[k].streak,
      streak_message: s.streak_message != null ? String(s.streak_message) : out[k].streak_message,
      title: pickNonEmptyString(s.title, s.rules_title, out[k].title),
      question_image: s.question_image != null ? String(s.question_image) : out[k].question_image,
      did_you_know_image: s.did_you_know_image != null ? String(s.did_you_know_image) : out[k].did_you_know_image,
      bonus_streak_badge: s.bonus_streak_badge != null ? String(s.bonus_streak_badge) : out[k].bonus_streak_badge,
      bonus_streak_heading: s.bonus_streak_heading != null ? String(s.bonus_streak_heading) : out[k].bonus_streak_heading,
      logo: pickNonEmptyString(s.logo, out[k].logo),
      description: pickDescriptionString(s.description, out[k].description),
    };
  }
  return out;
}

function clampWithWarn(value, max, fieldName) {
  if (String(value ?? "").length > max) {
    Swal.fire({
      title: "Character Limit Exceeded",
      text: `"${fieldName}" cannot exceed ${max} characters.`,
      icon: "warning",
      confirmButtonText: "OK",
    });
    return String(value).slice(0, max);
  }
  return value;
}

function validateImageFile(file, { width, height, label, allowJpeg = false, maxBytes } = {}) {
  if (!file) return null;
  const lower = file.name.toLowerCase();
  const isPng = file.type === "image/png" || lower.endsWith(".png");
  const isJpeg =
    allowJpeg &&
    (file.type === "image/jpeg" ||
      file.type === "image/jpg" ||
      lower.endsWith(".jpg") ||
      lower.endsWith(".jpeg"));
  if (!isPng && !isJpeg) {
    Swal.fire({
      title: "Invalid File Type",
      text: `Only ${allowJpeg ? "PNG or JPEG" : "PNG"} images${width && height ? ` (${width}×${height} px)` : ""} are accepted.`,
      icon: "error",
    });
    return null;
  }
  if (Number.isFinite(maxBytes) && file.size > maxBytes) {
    const maxMb = Math.round(maxBytes / (1024 * 1024));
    Swal.fire({
      title: "File Too Large",
      text: `${label ? `${label} ` : ""}must be at most ${maxMb} MB.`,
      icon: "error",
    });
    return null;
  }
  return file;
}

function handleImageChange(setter, previewSetter, { width, height, label, allowJpeg = false, maxBytes } = {}) {
  return (e) => {
    const inputEl = e.target;
    const raw = inputEl.files?.[0];
    if (!raw) return;
    const file = validateImageFile(raw, { width, height, label, allowJpeg, maxBytes });
    if (!file) {
      inputEl.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result;
      if (!dataUrl) {
        inputEl.value = "";
        return;
      }
      const img = new Image();
      img.onload = () => {
        const wantsSize = Number.isFinite(Number(width)) && Number.isFinite(Number(height));
        const ok = !wantsSize || (img.width === width && img.height === height);
        if (!ok && wantsSize) {
          Swal.fire({
            title: "Invalid Image Size",
            text: `Image must be exactly ${width}×${height} px.`,
            icon: "error",
          });
          inputEl.value = "";
          return;
        }
        setter(file);
        previewSetter(dataUrl);
        inputEl.value = "";
      };
      img.onerror = () => {
        Swal.fire({
          title: "Invalid Image",
          text: allowJpeg ? "Could not read this image file." : "Could not read this PNG file.",
          icon: "error",
        });
        inputEl.value = "";
      };
      img.src = String(dataUrl);
    };
    reader.onerror = () => {
      Swal.fire({
        title: "Invalid Image",
        text: allowJpeg ? "Could not read this image file." : "Could not read this PNG file.",
        icon: "error",
      });
      inputEl.value = "";
    };
    reader.readAsDataURL(file);
  };
}

function loadImageElement(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load puzzle image for slicing."));
    img.src = src;
  });
}

function normalizePuzzleAssetFilename(raw) {
  const value = String(raw ?? "").trim();
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) {
    try {
      const last = new URL(value).pathname.split("/").filter(Boolean).pop();
      return last ? decodeURIComponent(last) : "";
    } catch {
      return "";
    }
  }
  return value.replace(/^\/+/, "").split("/").pop() || "";
}

async function fetchRemoteImageAsObjectUrl(imageSrc, { assetFilename, adminToken } = {}) {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const filename = normalizePuzzleAssetFilename(assetFilename || imageSrc);

  if (backendUrl && filename && adminToken) {
    const res = await fetch(
      `${backendUrl}/themeAsset?asset=${encodeURIComponent(filename)}`,
      { headers: { Authorization: `Bearer ${adminToken}` } }
    );
    if (!res.ok) {
      const contentType = res.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.message || "Failed to load puzzle image.");
      }
      throw new Error("Failed to load puzzle image.");
    }
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  }

  if (imageSrc && /^https?:\/\//i.test(imageSrc)) {
    try {
      const res = await fetch(imageSrc);
      if (!res.ok) throw new Error("Failed to load puzzle image.");
      const blob = await res.blob();
      return URL.createObjectURL(blob);
    } catch (err) {
      if (err?.message === "Failed to load puzzle image.") throw err;
      throw new Error(err?.message || "Failed to load puzzle image.");
    }
  }

  throw new Error("Failed to load saved puzzle image. Please try again or re-upload.");
}

async function resolveImageSourceForSlicing(
  puzzleMainFile,
  puzzleMainPreview,
  savedPuzzleMainObjectUrl,
  { assetFilename, adminToken } = {}
) {
  if (puzzleMainPreview) return puzzleMainPreview;
  if (puzzleMainFile) return URL.createObjectURL(puzzleMainFile);
  if (savedPuzzleMainObjectUrl) return savedPuzzleMainObjectUrl;
  const filename = normalizePuzzleAssetFilename(assetFilename);
  if (filename && adminToken) {
    return fetchRemoteImageAsObjectUrl(null, { assetFilename: filename, adminToken });
  }
  return null;
}

async function resolvePreviewImageSrc(imageSrc, { assetFilename, adminToken } = {}) {
  if (!imageSrc) return null;
  if (imageSrc.startsWith("blob:") || imageSrc.startsWith("data:")) return imageSrc;
  return fetchRemoteImageAsObjectUrl(imageSrc, { assetFilename, adminToken });
}

function buildSliceSizes(total, count) {
  const sizes = [];
  let used = 0;
  for (let i = 0; i < count; i += 1) {
    const size = i < count - 1 ? Math.floor(total / count) : total - used;
    sizes.push(size);
    used += size;
  }
  return sizes;
}

async function sliceImageIntoPieces(imageSrc, parts) {
  const img = await loadImageElement(imageSrc);
  const aspectRatio = img.naturalWidth / img.naturalHeight;
  const { rows, cols } = computePuzzleGrid(parts, aspectRatio);
  const colWidths = buildSliceSizes(img.naturalWidth, cols);
  const rowHeights = buildSliceSizes(img.naturalHeight, rows);

  if (colWidths.some((w) => w < 1) || rowHeights.some((h) => h < 1)) {
    throw new Error("Image is too small to divide into the selected number of pieces.");
  }

  const files = [];
  const dataUrls = [];
  let sy = 0;
  for (let row = 0; row < rows; row++) {
    const pieceHeight = rowHeights[row];
    let sx = 0;
    for (let col = 0; col < cols; col++) {
      const pieceWidth = colWidths[col];
      const canvas = document.createElement("canvas");
      canvas.width = pieceWidth;
      canvas.height = pieceHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(
        img,
        sx,
        sy,
        pieceWidth,
        pieceHeight,
        0,
        0,
        pieceWidth,
        pieceHeight
      );
      sx += pieceWidth;
      dataUrls.push(canvas.toDataURL("image/jpeg", 0.92));
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error("Failed to create puzzle piece image."))),
          "image/jpeg",
          0.92
        );
      });
      const partIndex = row * cols + col + 1;
      files.push(new File([blob], `puzzle_part_${partIndex}.jpg`, { type: "image/jpeg" }));
    }
    sy += pieceHeight;
  }
  return { files, dataUrls, rows, cols };
}

function PuzzlePartsPreview({
  imageSrc,
  parts,
  savedPieceUrls,
  useSavedPieces = false,
  assetFilename = "",
  adminToken = "",
}) {
  const [aspectRatio, setAspectRatio] = useState(4 / 3);
  const [pieceUrls, setPieceUrls] = useState([]);
  const [gridRows, setGridRows] = useState(3);
  const [gridCols, setGridCols] = useState(3);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const { rows, cols } = useMemo(
    () => computePuzzleGrid(parts, aspectRatio),
    [parts, aspectRatio]
  );

  useEffect(() => {
    if (!imageSrc) {
      setAspectRatio(4 / 3);
      return undefined;
    }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        setAspectRatio(img.naturalWidth / img.naturalHeight);
      }
    };
    img.src = imageSrc;
    return () => {
      img.onload = null;
    };
  }, [imageSrc]);

  const savedPiecesMatchParts =
    useSavedPieces && savedPieceUrls?.length > 0 && savedPieceUrls.length === parts;

  useEffect(() => {
    if (!imageSrc) {
      if (savedPiecesMatchParts) {
        setPieceUrls(savedPieceUrls);
        setGridRows(rows);
        setGridCols(cols);
      } else {
        setPieceUrls([]);
      }
      setLoading(false);
      setError("");
      return undefined;
    }

    if (savedPiecesMatchParts) {
      setPieceUrls(savedPieceUrls);
      setGridRows(rows);
      setGridCols(cols);
      setLoading(false);
      setError("");
      return undefined;
    }

    let cancelled = false;
    let sliceBlobUrl = null;
    setLoading(true);
    setError("");
    setPieceUrls([]);

    (async () => {
      try {
        const sliceSrc = await resolvePreviewImageSrc(imageSrc, { assetFilename, adminToken });
        if (cancelled) {
          if (sliceSrc?.startsWith("blob:") && sliceSrc !== imageSrc) {
            URL.revokeObjectURL(sliceSrc);
          }
          return;
        }
        if (sliceSrc?.startsWith("blob:") && sliceSrc !== imageSrc) {
          sliceBlobUrl = sliceSrc;
        }
        const { dataUrls, rows: r, cols: c } = await sliceImageIntoPieces(sliceSrc, parts);
        if (cancelled) return;
        setPieceUrls(dataUrls);
        setGridRows(r);
        setGridCols(c);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setPieceUrls([]);
        setLoading(false);
        setError(err?.message || "Could not preview puzzle pieces.");
      }
    })();

    return () => {
      cancelled = true;
      if (sliceBlobUrl) {
        URL.revokeObjectURL(sliceBlobUrl);
      }
    };
  }, [imageSrc, parts, savedPieceUrls, savedPiecesMatchParts, useSavedPieces, rows, cols, assetFilename, adminToken]);

  const displayUrls = pieceUrls;

  return (
    <div style={{ padding: 16, background: "#f8f9fa", borderRadius: 10, border: "1px solid #dee2e6" }}>
      <div style={{ fontSize: 13, color: "#6c757d", marginBottom: 10 }}>
        Cut pieces: <strong>{rows} rows × {cols} columns</strong> = {parts} pieces
      </div>
      {loading ? (
        <div style={{ fontSize: 13, color: "#6c757d", padding: "12px 0" }}>Generating piece preview…</div>
      ) : null}
      {error ? (
        <div style={{ fontSize: 13, color: "#c62828", padding: "8px 0" }}>{error}</div>
      ) : null}
      {displayUrls.length > 0 ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
            gap: 6,
            maxWidth: 360,
          }}
        >
          {displayUrls.map((url, i) => (
            <div
              key={i}
              style={{
                borderRadius: 6,
                border: "2px solid #fff",
                boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
                overflow: "hidden",
                background: "#e5e7eb",
              }}
            >
              <img
                src={url}
                alt="Puzzle piece"
                style={{ width: "100%", height: "auto", display: "block" }}
              />
            </div>
          ))}
        </div>
      ) : null}
      {!loading && !imageSrc && displayUrls.length === 0 ? (
        <div style={{ fontSize: 12, color: "#999", marginTop: 8, fontStyle: "italic" }}>
          Upload an image above to see cut pieces
        </div>
      ) : null}
    </div>
  );
}

function CharCount({ value, max }) {
  const len = String(value ?? "").length;
  const near = len >= max * 0.85;
  const over = len > max;
  return (
    <div
      style={{
        textAlign: "right",
        fontSize: 12,
        marginTop: 2,
        color: over ? "#c62828" : near ? "#e65100" : "#6c757d",
        fontWeight: over ? 700 : 400,
      }}
    >
      {len}/{max}
    </div>
  );
}

function Day3AnswerGroup({ title, hint, kind, answers, disabled, onAdd, onRemove }) {
  const [input, setInput] = useState("");
  const list = Array.isArray(answers) ? answers : [];
  const isRejected = kind === "rejected";

  const commit = () => {
    const value = input.trim();
    if (!value) return;
    onAdd(kind, value);
    setInput("");
  };

  return (
    <div className="themeupdate-topic-answers-group">
      <div className="themeupdate-help" style={{ marginBottom: 6 }}>
        <strong style={{ color: isRejected ? "#c62828" : "#1b7f3b" }}>{title}</strong>
        {hint ? <span style={{ marginLeft: 6, color: "#6c757d" }}>{hint}</span> : null}
      </div>
      {list.length > 0 ? (
        <div className="themeupdate-word-tags" role="list" style={{ marginBottom: 8 }}>
          {list.map((answer, answerIdx) => (
            <span
              key={`${kind}-${answer}-${answerIdx}`}
              className={`themeupdate-word-tag${isRejected ? " themeupdate-word-tag--rejected" : ""}`}
              role="listitem"
            >
              {answer}
              <button
                type="button"
                className="themeupdate-word-tag-remove"
                onClick={() => onRemove(kind, answerIdx)}
                aria-label={`Remove ${answer}`}
                disabled={disabled}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      ) : null}
      <div className="themeupdate-day1-add-word">
        <input
          type="text"
          className="themeupdate-input"
          value={input}
          maxLength={DAY3_ANSWER_CHAR_MAX}
          placeholder={
            isRejected ? "Add a word that is NOT accepted" : "Add an acceptable answer"
          }
          disabled={disabled}
          onChange={(e) => setInput(e.target.value.slice(0, DAY3_ANSWER_CHAR_MAX))}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit();
            }
          }}
        />
        <button
          type="button"
          className="themeupdate-btn themeupdate-btn-sm themeupdate-btn-neutral-outline"
          onClick={commit}
          disabled={disabled || !input.trim()}
        >
          Add
        </button>
      </div>
    </div>
  );
}

function Day3TopicCard({ topic, index, disabled, onRemoveTopic, onAddAnswer, onRemoveAnswer }) {
  const word = cleanTopicLabel(topic?.word);
  const answers = Array.isArray(topic?.answers) ? topic.answers : [];
  const rejected = Array.isArray(topic?.rejected_answers) ? topic.rejected_answers : [];

  return (
    <div className="themeupdate-topic-card">
      <div className="themeupdate-topic-card-head">
        <span className="themeupdate-topic-card-title">
          <span className="themeupdate-topic-card-index">{index + 1}.</span> {word}
        </span>
        <button
          type="button"
          className="themeupdate-btn themeupdate-btn-sm themeupdate-btn-danger"
          onClick={() => onRemoveTopic(index)}
          aria-label={`Remove ${word}`}
        >
          Remove topic
        </button>
      </div>
      <Day3AnswerGroup
        title="Acceptable answers"
        hint="Only these count as correct. Leave empty to accept any real word."
        kind="acceptable"
        answers={answers}
        disabled={disabled}
        onAdd={(kind, value) => onAddAnswer(index, kind, value)}
        onRemove={(kind, answerIdx) => onRemoveAnswer(index, kind, answerIdx)}
      />
      <Day3AnswerGroup
        title="Not acceptable answers"
        hint="These are always rejected, even if they are real words."
        kind="rejected"
        answers={rejected}
        disabled={disabled}
        onAdd={(kind, value) => onAddAnswer(index, kind, value)}
        onRemove={(kind, answerIdx) => onRemoveAnswer(index, kind, answerIdx)}
      />
    </div>
  );
}

export default function ThemeUpdate() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const [activeDay, setActiveDay] = useState(1);
  const [savingDay, setSavingDay] = useState(false);
  const [savingBadges, setSavingBadges] = useState(false);
  const [savingRulesAndQuestions, setSavingRulesAndQuestions] = useState(false);
  const [stagesData, setStagesData] = useState(() => normalizeStagesData(null));
  const [questionsPairs, setQuestionsPairs] = useState([]);
  const [badgeFile, setBadgeFile] = useState(null);
  const [streakBadgeFile, setStreakBadgeFile] = useState(null);
  const [bonusStreakBadgeFile, setBonusStreakBadgeFile] = useState(null);
  const [questionImageFile, setQuestionImageFile] = useState(null);
  const [didYouKnowImageFile, setDidYouKnowImageFile] = useState(null);
  const [badgePreview, setBadgePreview] = useState(null);
  const [streakBadgePreview, setStreakBadgePreview] = useState(null);
  const [bonusStreakBadgePreview, setBonusStreakBadgePreview] = useState(null);
  const [questionImagePreview, setQuestionImagePreview] = useState(null);
  const [didYouKnowImagePreview, setDidYouKnowImagePreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [day1WordInput, setDay1WordInput] = useState("");
  const [day1WordType, setDay1WordType] = useState("good");
  const day1ExcelInputRef = useRef(null);
  const day3ExcelInputRef = useRef(null);
  const puzzleMainInputRef = useRef(null);

  const [activePuzzleTab, setActivePuzzleTab] = useState(false);
  const [puzzleMainFile, setPuzzleMainFile] = useState(null);
  const [puzzleMainPreview, setPuzzleMainPreview] = useState(null);
  const [savingPuzzle, setSavingPuzzle] = useState(false);
  const [puzzleParts, setPuzzleParts] = useState(9);
  const [puzzlePartsInput, setPuzzlePartsInput] = useState("9");
  const [puzzlePartsBaseline, setPuzzlePartsBaseline] = useState(9);
  const [puzzlePiecePreviews, setPuzzlePiecePreviews] = useState([]);
  const [puzzlePiecePreviewParts, setPuzzlePiecePreviewParts] = useState(null);
  const [savedPuzzleMainObjectUrl, setSavedPuzzleMainObjectUrl] = useState(null);
  const [savedPuzzleMainLoading, setSavedPuzzleMainLoading] = useState(false);
  const [savedPuzzleMainError, setSavedPuzzleMainError] = useState("");
  const puzzleSettingsDirtyRef = useRef(false);

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: currentTheme || null, isAdmin: true }));
  }, [dispatch, currentTheme]);

  puzzleSettingsDirtyRef.current =
    puzzleParts !== puzzlePartsBaseline ||
    Boolean(puzzleMainPreview) ||
    Boolean(puzzleMainFile);

  useEffect(() => {
    if (!data) return;
    setStagesData(normalizeStagesData(data.stages_data));
    if (puzzleSettingsDirtyRef.current) return;
    const partsCount = resolvePuzzlePartsCount(data);
    setPuzzleParts(partsCount);
    setPuzzlePartsInput(String(partsCount));
    setPuzzlePartsBaseline(partsCount);
    setPuzzlePiecePreviews([]);
    setPuzzlePiecePreviewParts(null);
  }, [data]);

  const dayTabs = useMemo(() => Array.from({ length: DAY_COUNT }).map((_, i) => i + 1), []);

  const fetchedStages = useMemo(
    () => normalizeStagesData(data?.stages_data),
    [data?.stages_data]
  );

  const stageKey = `stage_${activeDay}`;
  const stageDefaults = useMemo(
    () => normalizeStagesData(null)[stageKey],
    [stageKey]
  );
  const stage = useMemo(() => {
    const fromFetch = fetchedStages[stageKey];
    const fromState = stagesData[stageKey];
    if (!isEmptyStage(fromState)) {
      return { ...stageDefaults, ...fromFetch, ...fromState };
    }
    return { ...stageDefaults, ...fromFetch };
  }, [fetchedStages, stagesData, stageKey, stageDefaults]);
  const cfg = DAY_CONFIG[activeDay] || DAY_CONFIG[1];

  const effectiveMaxQuestions = useMemo(() => {
    const raw = stage?.max_questions;
    if (raw === "") return cfg.maxQ;
    const n = Math.floor(Number(raw));
    if (!Number.isFinite(n)) return cfg.maxQ;
    return Math.min(cfg.maxQ, Math.max(1, n));
  }, [stage?.max_questions, cfg.maxQ]);

  const savedBadgeUrl = useMemo(() => s3PublicUrl(stage?.badge), [stage?.badge]);
  const savedStreakUrl = useMemo(() => s3PublicUrl(stage?.streak), [stage?.streak]);
  const savedBonusStreakUrl = useMemo(() => s3PublicUrl(stage?.bonus_streak_badge), [stage?.bonus_streak_badge]);
  const savedQuestionImageUrl = useMemo(() => s3PublicUrl(stage?.question_image), [stage?.question_image]);
  const savedDidYouKnowImageUrl = useMemo(() => s3PublicUrl(stage?.did_you_know_image), [stage?.did_you_know_image]);
  const savedLogoUrl = useMemo(() => s3PublicUrl(stage?.logo), [stage?.logo]);

  const savedTotalPuzzles = useMemo(() => {
    if (!data) return null;
    return resolvePuzzlePartsCount(data);
  }, [data]);

  const savedPuzzleImageFilename = useMemo(
    () => normalizePuzzleAssetFilename(data?.puzzle_image),
    [data?.puzzle_image]
  );
  const savedPuzzleMainUrl = useMemo(() => s3PublicUrl(data?.puzzle_image), [data?.puzzle_image]);
  const savedPuzzlePartUrls = useMemo(() => {
    return parsePuzzleImgArray(data?.img_array).map((filename) => s3PublicUrl(filename)).filter(Boolean);
  }, [data?.img_array]);

  const resolvedPuzzleMainSrc = puzzleMainPreview || savedPuzzleMainObjectUrl || savedPuzzleMainUrl;
  const puzzleMainDisplaySrc = puzzleMainPreview || savedPuzzleMainUrl || savedPuzzleMainObjectUrl;

  useEffect(() => {
    if (puzzleMainPreview || puzzleMainFile || !savedPuzzleImageFilename || !adminToken) {
      setSavedPuzzleMainObjectUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setSavedPuzzleMainLoading(false);
      setSavedPuzzleMainError("");
      return undefined;
    }

    let cancelled = false;
    let blobUrl = null;
    setSavedPuzzleMainLoading(true);
    setSavedPuzzleMainError("");

    fetchRemoteImageAsObjectUrl(null, {
      assetFilename: savedPuzzleImageFilename,
      adminToken,
    })
      .then((url) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        blobUrl = url;
        setSavedPuzzleMainObjectUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });
        setSavedPuzzleMainLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setSavedPuzzleMainObjectUrl(null);
        setSavedPuzzleMainLoading(false);
        setSavedPuzzleMainError(err?.message || "Could not load saved puzzle image.");
      });

    return () => {
      cancelled = true;
    };
  }, [adminToken, puzzleMainFile, puzzleMainPreview, savedPuzzleImageFilename]);

  const puzzlePartsDirty =
    puzzleParts !== puzzlePartsBaseline || Boolean(puzzleMainPreview) || Boolean(puzzleMainFile);

  const useSavedPuzzlePieces =
    !puzzlePartsDirty &&
    savedPuzzlePartUrls.length > 0 &&
    savedPuzzlePartUrls.length === puzzleParts &&
    puzzleParts === savedTotalPuzzles;

  useEffect(() => {
    const normalized = normalizeStagesData(data?.stages_data);
    const key = `stage_${activeDay}`;
    const dayStage = normalized[key];
    const qs = Array.isArray(dayStage?.questions) ? dayStage.questions : [];
    setQuestionsPairs(qs);
    if (data && dayStage && !isEmptyStage(dayStage)) {
      setStagesData((prev) => {
        if (!isEmptyStage(prev[key])) return prev;
        return { ...prev, [key]: dayStage };
      });
    }
    // Reset any *selected* (not-yet-saved) image files when switching day so saved data stays intact.
    setBadgeFile(null);
    setStreakBadgeFile(null);
    setBonusStreakBadgeFile(null);
    setQuestionImageFile(null);
    setDidYouKnowImageFile(null);
    setBadgePreview(null);
    setStreakBadgePreview(null);
    setBonusStreakBadgePreview(null);
    setQuestionImagePreview(null);
    setDidYouKnowImagePreview(null);
    setLogoFile(null);
    setLogoPreview(null);
    setDay1WordInput("");
    setDay1WordType("good");
  }, [activeDay, data]);

  const dayWordListMax = activeDay === 5 ? DAY1_WORD_MAX : activeDay === 3 ? DAY3_TOPIC_MAX : 0;
  const getDayWordLabel = (q) =>
    activeDay === 5 ? cleanWordleWord(q?.word) : activeDay === 3 ? cleanTopicLabel(q?.word) : "";

  const updateStage = (partial) => {
    setStagesData((prev) => ({
      ...prev,
      [stageKey]: {
        ...stageDefaults,
        ...fetchedStages[stageKey],
        ...prev[stageKey],
        ...partial,
      },
    }));
  };

  const updatePairCell = (idx, partial) => {
    setQuestionsPairs((prev) => {
      const next = Array.isArray(prev) ? [...prev] : [];
      const base = next[idx] && typeof next[idx] === "object" ? next[idx] : {};
      next[idx] = { ...base, ...partial };
      return next;
    });
  };

  // MCQ option helpers
  const updateOption = (pairIdx, optionIdx, value) => {
    const clamped = clampWithWarn(value, cfg.optionMax, `Option ${optionIdx + 1}`);
    setQuestionsPairs((prev) => {
      const next = [...prev];
      const options = Array.isArray(next[pairIdx]?.options) ? [...next[pairIdx].options] : [];
      options[optionIdx] = clamped;
      next[pairIdx] = { ...next[pairIdx], options };
      return next;
    });
  };

  const addOption = (pairIdx) => {
    setQuestionsPairs((prev) => {
      const next = [...prev];
      const options = Array.isArray(next[pairIdx]?.options) ? [...next[pairIdx].options] : [];
      if (options.length >= 4) {
        Swal.fire("Limit Reached", "A question can have at most 4 options.", "warning");
        return prev;
      }
      options.push("");
      next[pairIdx] = { ...next[pairIdx], options };
      return next;
    });
  };

  const removeOption = (pairIdx, optionIdx) => {
    setQuestionsPairs((prev) => {
      const next = [...prev];
      const options = Array.isArray(next[pairIdx]?.options) ? [...next[pairIdx].options] : [];
      if (options.length <= 2) {
        Swal.fire("Minimum Options", "A question must have at least 2 options.", "warning");
        return prev;
      }
      options.splice(optionIdx, 1);
      next[pairIdx] = { ...next[pairIdx], options };
      return next;
    });
  };

  const addPair = () => {
    const currentCount = Array.isArray(questionsPairs) ? questionsPairs.length : 0;
    const limit =
      activeDay === 5 ? DAY1_WORD_MAX : activeDay === 3 ? DAY3_TOPIC_MAX : effectiveMaxQuestions;
    if (currentCount >= limit) {
      Swal.fire({
        title: activeDay === 5 || activeDay === 3 ? "Word Limit Reached" : "Question Limit Reached",
        text:
          activeDay === 5
            ? `Stage 5 allows a maximum of ${DAY1_WORD_MAX} words.`
            : activeDay === 3
              ? `Stage 3 allows a maximum of ${DAY3_TOPIC_MAX} words.`
              : `Stage ${activeDay} allows a maximum of ${effectiveMaxQuestions} question${effectiveMaxQuestions > 1 ? "s" : ""}.`,
        icon: "warning",
      });
      return;
    }
    let newRow;
    if (cfg.type === "wordle" || cfg.type === "category_rush") {
      newRow = { word: "", did_you_know: "" };
    } else if (cfg.type === "mcq") {
      newRow = { question: "", options: ["", ""], answer: "", did_you_know: "" };
    } else {
      newRow = { question: "", answer: "", did_you_know: "" };
    }
    setQuestionsPairs((prev) => [...(Array.isArray(prev) ? prev : []), newRow]);
  };

  const deletePair = (idx) => {
    setQuestionsPairs((prev) => {
      const next = Array.isArray(prev) ? [...prev] : [];
      next.splice(idx, 1);
      return next;
    });
  };

  // Clear All removed by request (keep per-row delete only).

  const updateRuleCell = (idx, val) => {
    if (String(val ?? "").length > 200) {
      Swal.fire("Limit Exceeded", "Rules can be at most 200 characters.", "warning");
      val = String(val ?? "").slice(0, 200);
    }
    const rules = Array.isArray(stage.rules) ? [...stage.rules] : [""];
    rules[idx] = val;
    updateStage({ rules });
  };

  const addRule = () => {
    const rules = Array.isArray(stage.rules) ? [...stage.rules] : [""];
    if (rules.length >= 6) {
      Swal.fire("Limit Reached", "You can only add up to 6 rules.", "warning");
      return;
    }
    rules.push("");
    updateStage({ rules });
  };

  const removeRule = (idx) => {
    const rules = Array.isArray(stage.rules) ? [...stage.rules] : [""];
    if (rules.length <= 1) {
      Swal.fire("At Least One Rule Required", "You must have at least one rule.", "warning");
      return;
    }
    rules.splice(idx, 1);
    updateStage({ rules });
  };

  // Validate all Q&A pairs before saving
  const validatePairs = (pairs) => {
    for (let i = 0; i < pairs.length; i++) {
      const q = pairs[i];
      const num = i + 1;
      if (cfg.type === "wordle") {
        const w = cleanWordleWord(q?.word);
        if (!w || w.length > cfg.questionMax) {
          Swal.fire(
            "Validation Error",
            `Word ${num}: must be 1–${cfg.questionMax} characters (${DAY1_WORD_ALLOWED_HINT}).`,
            "error"
          );
          return false;
        }
      } else if (cfg.type === "category_rush") {
        const w = cleanTopicLabel(q?.word);
        if (!w || w.length > cfg.questionMax) {
          Swal.fire(
            "Validation Error",
            `Word ${num}: must be 1–${cfg.questionMax} characters.`,
            "error"
          );
          return false;
        }
      } else if (cfg.type === "mcq") {
        if (String(q?.question ?? "").length > cfg.questionMax) {
          Swal.fire("Validation Error", `Question ${num}: Question text exceeds ${cfg.questionMax} characters.`, "error");
          return false;
        }
        if (String(q?.answer ?? "").length > cfg.answerMax) {
          Swal.fire("Validation Error", `Question ${num}: Answer exceeds ${cfg.answerMax} characters.`, "error");
          return false;
        }
        const options = Array.isArray(q?.options) ? q.options : [];
        if (options.length < 2 || options.length > 4) {
          Swal.fire("Validation Error", `Question ${num}: Options must be between 2 and 4.`, "error");
          return false;
        }
        const norm = (s) => String(s ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
        const seen = new Set();
        for (let o = 0; o < options.length; o++) {
          const v = String(options[o] ?? '');
          if (v.trim() === '') {
            Swal.fire("Validation Error", `Question ${num}: Option ${o + 1} cannot be blank.`, "error");
            return false;
          }
          const key = norm(v);
          if (seen.has(key)) {
            Swal.fire("Validation Error", `Question ${num}: Duplicate options are not allowed.`, "error");
            return false;
          }
          seen.add(key);
        }
        for (let o = 0; o < options.length; o++) {
          if (String(options[o] ?? "").length > cfg.optionMax) {
            Swal.fire("Validation Error", `Question ${num}, Option ${o + 1}: exceeds ${cfg.optionMax} characters.`, "error");
            return false;
          }
        }
        const ans = String(q?.answer ?? '');
        if (ans.trim() === '') {
          Swal.fire("Validation Error", `Question ${num}: Answer cannot be blank.`, "error");
          return false;
        }
        const matches = options.filter((opt) => norm(opt) === norm(ans)).length;
        if (matches !== 1) {
          Swal.fire(
            "Validation Error",
            `Question ${num}: Answer must match exactly one option (no duplicates).`,
            "error"
          );
          return false;
        }
      } else {
        if (String(q?.question ?? "").length > cfg.questionMax) {
          Swal.fire("Validation Error", `Question ${num}: Question text exceeds ${cfg.questionMax} characters.`, "error");
          return false;
        }
        if (String(q?.answer ?? "").length > cfg.answerMax) {
          Swal.fire("Validation Error", `Question ${num}: Answer exceeds ${cfg.answerMax} characters.`, "error");
          return false;
        }
      }
      if (String(q?.did_you_know ?? "").length > DID_YOU_KNOW_MAX) {
        Swal.fire("Validation Error", `Question ${num}: "Did You Know" exceeds ${DID_YOU_KNOW_MAX} characters.`, "error");
        return false;
      }
    }
    return true;
  };

  const handleSaveBadges = async () => {
    if (!adminToken) {
      Swal.fire("Error", "Session expired. Please log in again.", "error");
      return;
    }

    if (!badgeFile && !streakBadgeFile && !bonusStreakBadgeFile && !questionImageFile && !didYouKnowImageFile && !logoFile) {
      Swal.fire("Warning", "Please select at least one image to upload.", "warning");
      return;
    }

    try {
      setSavingBadges(true);
      const formData = new FormData();
      if (badgeFile) formData.append("badge", badgeFile);
      if (streakBadgeFile) formData.append("streak", streakBadgeFile);
      if (bonusStreakBadgeFile) formData.append("bonus_streak", bonusStreakBadgeFile);
      if (questionImageFile) formData.append("question_image", questionImageFile);
      if (didYouKnowImageFile) formData.append("did_you_know_image", didYouKnowImageFile);
      if (logoFile) formData.append("logo", logoFile);
      formData.append("day", activeDay);
      formData.append("currentTheme", currentTheme);
      formData.append("organizationId", organizationId || "admin");
      formData.append("sessionId", sessionId || "admin");

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/uploadBadges`, {
        method: "POST",
        headers: { Authorization: `Bearer ${adminToken}` },
        body: formData,
      });

      if (!response.ok) throw new Error(`Upload failed with status ${response.status}`);

      Swal.fire("Success", "Badges saved successfully.", "success");
      setBadgeFile(null); setStreakBadgeFile(null); setBonusStreakBadgeFile(null);
      setQuestionImageFile(null); setDidYouKnowImageFile(null);
      setBadgePreview(null); setStreakBadgePreview(null); setBonusStreakBadgePreview(null);
      setQuestionImagePreview(null); setDidYouKnowImagePreview(null);
      setLogoFile(null); setLogoPreview(null);
      dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to save badges.", "error");
    } finally {
      setSavingBadges(false);
    }
  };

  const handleAddDay1Word = () => {
    const word = cleanWordleWord(day1WordInput);
    if (!word || word.length > DAY1_WORD_CHAR_MAX) {
      Swal.fire("Invalid word", `Each word must be 1–${DAY1_WORD_CHAR_MAX} characters (${DAY1_WORD_ALLOWED_HINT}).`, "warning");
      return;
    }
    const current = Array.isArray(questionsPairs) ? questionsPairs : [];
    if (current.length >= DAY1_WORD_MAX) {
      Swal.fire("Word Limit Reached", `Stage 5 allows a maximum of ${DAY1_WORD_MAX} words.`, "warning");
      return;
    }
    if (current.some((q) => cleanWordleWord(q?.word) === word)) {
      Swal.fire("Duplicate word", "This word is already in the list.", "warning");
      return;
    }
    setQuestionsPairs([...current, { word, type: normalizeDay1WordType(day1WordType), did_you_know: "" }]);
    setDay1WordInput("");
  };

  const handleAddDay3Word = () => {
    const word = cleanTopicLabel(day1WordInput);
    if (!word || word.length > DAY3_TOPIC_CHAR_MAX) {
      Swal.fire(
        "Invalid word",
        `Each word must be 1–${DAY3_TOPIC_CHAR_MAX} characters.`,
        "warning"
      );
      return;
    }
    const current = Array.isArray(questionsPairs) ? questionsPairs : [];
    if (current.length >= DAY3_TOPIC_MAX) {
      Swal.fire("Word Limit Reached", `Stage 3 allows a maximum of ${DAY3_TOPIC_MAX} words.`, "warning");
      return;
    }
    if (current.some((q) => cleanTopicLabel(q?.word).toLowerCase() === word.toLowerCase())) {
      Swal.fire("Duplicate word", "This word is already in the list.", "warning");
      return;
    }
    setQuestionsPairs([...current, { word, answers: [], rejected_answers: [], did_you_know: "" }]);
    setDay1WordInput("");
  };

  const addDay3TopicAnswer = (idx, kind, value) => {
    const clean = cleanTopicAnswer(value);
    if (!clean) return;
    const field = kind === "rejected" ? "rejected_answers" : "answers";
    setQuestionsPairs((prev) => {
      const next = Array.isArray(prev) ? [...prev] : [];
      const base = next[idx] && typeof next[idx] === "object" ? next[idx] : {};
      const list = Array.isArray(base[field]) ? base[field] : [];
      if (list.length >= DAY3_ANSWERS_PER_TOPIC_MAX) {
        Swal.fire(
          "Answer Limit Reached",
          `Each topic allows up to ${DAY3_ANSWERS_PER_TOPIC_MAX} answers.`,
          "warning"
        );
        return prev;
      }
      if (list.some((a) => String(a).toLowerCase() === clean.toLowerCase())) {
        return prev;
      }
      next[idx] = { ...base, [field]: [...list, clean] };
      return next;
    });
  };

  const removeDay3TopicAnswer = (idx, kind, answerIdx) => {
    const field = kind === "rejected" ? "rejected_answers" : "answers";
    setQuestionsPairs((prev) => {
      const next = Array.isArray(prev) ? [...prev] : [];
      const base = next[idx] && typeof next[idx] === "object" ? next[idx] : {};
      const list = Array.isArray(base[field]) ? [...base[field]] : [];
      list.splice(answerIdx, 1);
      next[idx] = { ...base, [field]: list };
      return next;
    });
  };

  const handleSaveDay1All = async () => {
    if (!adminToken) {
      Swal.fire("Error", "Session expired. Please log in again.", "error");
      return;
    }
    if (!validateStageTimerValue(stage.timer, 1)) return;

    const rulesClean = (Array.isArray(stage.rules) ? stage.rules : [])
      .map((r) => String(r ?? "").trim())
      .filter(Boolean);
    if (rulesClean.length === 0) {
      Swal.fire({ title: "Validation", text: "Add at least one game rule.", icon: "error" });
      return;
    }
    if (String(stage.title ?? "").length > TITLE_MAX) {
      Swal.fire("Validation Error", `Title cannot exceed ${TITLE_MAX} characters.`, "error");
      return;
    }
    if (descriptionToTextareaMultiline(stage.description).length > getDayDescriptionMax(1)) {
      Swal.fire("Validation Error", `Description cannot exceed ${getDayDescriptionMax(1)} characters.`, "error");
      return;
    }

    try {
      setSavingDay(true);
      let logoPath = String(stage.logo ?? "").trim();

      if (logoFile) {
        const formData = new FormData();
        formData.append("logo", logoFile);
        formData.append("day", 1);
        formData.append("currentTheme", currentTheme);
        formData.append("organizationId", organizationId || "admin");
        formData.append("sessionId", sessionId || "admin");

        const uploadRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/uploadBadges`, {
          method: "POST",
          headers: { Authorization: `Bearer ${adminToken}` },
          body: formData,
        });
        const uploadJson = await uploadRes.json().catch(() => ({}));
        if (!uploadRes.ok) {
          throw new Error(uploadJson.message || `Logo upload failed with status ${uploadRes.status}`);
        }
        if (uploadJson.uploadedBadges?.logo) {
          logoPath = String(uploadJson.uploadedBadges.logo);
        }
      }

      const stageOut = {
        ...stage,
        type: cfg.type,
        rules: rulesClean.slice(0, 6),
        title: String(stage.title ?? "").trim(),
        description: String(stage.description ?? "").trim(),
        timer: String(stage.timer ?? STAGE_DEFAULT_TIMER).trim() || STAGE_DEFAULT_TIMER,
        logo: logoPath,
      };

      const nextStages = { ...stagesData, stage_1: stageOut };
      await updateThemeData({
        payload: {
          data: { stages_data: nextStages },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });

      Swal.fire("Saved", "Stage 1 saved successfully.", "success");
      setLogoFile(null);
      setLogoPreview(null);
      dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to save Stage 1.", "error");
    } finally {
      setSavingDay(false);
    }
  };

  const handleSaveDay2All = async () => {
    if (!adminToken) {
      Swal.fire("Error", "Session expired. Please log in again.", "error");
      return;
    }
    if (!validateStageTimerValue(stage.timer, 2)) return;

    const rulesClean = (Array.isArray(stage.rules) ? stage.rules : [])
      .map((r) => String(r ?? "").trim())
      .filter(Boolean);
    if (rulesClean.length === 0) {
      Swal.fire({ title: "Validation", text: "Add at least one game rule.", icon: "error" });
      return;
    }
    if (String(stage.title ?? "").length > TITLE_MAX) {
      Swal.fire("Validation Error", `Title cannot exceed ${TITLE_MAX} characters.`, "error");
      return;
    }
    if (descriptionToTextareaMultiline(stage.description).length > getDayDescriptionMax(2)) {
      Swal.fire("Validation Error", `Description cannot exceed ${getDayDescriptionMax(2)} characters.`, "error");
      return;
    }

    try {
      setSavingDay(true);
      let logoPath = String(stage.logo ?? "").trim();

      if (logoFile) {
        const formData = new FormData();
        formData.append("logo", logoFile);
        formData.append("day", 2);
        formData.append("currentTheme", currentTheme);
        formData.append("organizationId", organizationId || "admin");
        formData.append("sessionId", sessionId || "admin");

        const uploadRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/uploadBadges`, {
          method: "POST",
          headers: { Authorization: `Bearer ${adminToken}` },
          body: formData,
        });
        const uploadJson = await uploadRes.json().catch(() => ({}));
        if (!uploadRes.ok) {
          throw new Error(uploadJson.message || `Logo upload failed with status ${uploadRes.status}`);
        }
        if (uploadJson.uploadedBadges?.logo) {
          logoPath = String(uploadJson.uploadedBadges.logo);
        }
      }

      const stageOut = {
        ...stage,
        type: cfg.type,
        rules: rulesClean.slice(0, 6),
        title: String(stage.title ?? "").trim(),
        description: String(stage.description ?? "").trim(),
        timer: String(stage.timer ?? STAGE_DEFAULT_TIMER).trim() || STAGE_DEFAULT_TIMER,
        logo: logoPath,
      };

      const nextStages = { ...stagesData, stage_2: stageOut };
      await updateThemeData({
        payload: {
          data: { stages_data: nextStages },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });

      Swal.fire("Saved", "Stage 2 saved successfully.", "success");
      setLogoFile(null);
      setLogoPreview(null);
      dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to save Stage 2.", "error");
    } finally {
      setSavingDay(false);
    }
  };

  const handleSaveDay3All = async () => {
    if (!adminToken) {
      Swal.fire("Error", "Session expired. Please log in again.", "error");
      return;
    }
    if (!validateStageTimerValue(stage.timer, 3)) return;

    const questions = (Array.isArray(questionsPairs) ? questionsPairs : []).filter((q) =>
      cleanTopicLabel(q?.word)
    );
    if (!validatePairs(questions)) return;
    if (questions.length === 0) {
      Swal.fire("Validation", "Add at least one word.", "error");
      return;
    }
    if (questions.length > DAY3_TOPIC_MAX) {
      Swal.fire("Too Many Words", `Stage 3 allows a maximum of ${DAY3_TOPIC_MAX} words.`, "error");
      return;
    }

    const rulesClean = (Array.isArray(stage.rules) ? stage.rules : [])
      .map((r) => String(r ?? "").trim())
      .filter(Boolean);
    if (rulesClean.length === 0) {
      Swal.fire({ title: "Validation", text: "Add at least one game rule.", icon: "error" });
      return;
    }
    if (String(stage.title ?? "").length > TITLE_MAX) {
      Swal.fire("Validation Error", `Title cannot exceed ${TITLE_MAX} characters.`, "error");
      return;
    }

    try {
      setSavingDay(true);
      let logoPath = String(stage.logo ?? "").trim();

      if (logoFile) {
        const formData = new FormData();
        formData.append("logo", logoFile);
        formData.append("day", 3);
        formData.append("currentTheme", currentTheme);
        formData.append("organizationId", organizationId || "admin");
        formData.append("sessionId", sessionId || "admin");

        const uploadRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/uploadBadges`, {
          method: "POST",
          headers: { Authorization: `Bearer ${adminToken}` },
          body: formData,
        });
        const uploadJson = await uploadRes.json().catch(() => ({}));
        if (!uploadRes.ok) {
          throw new Error(uploadJson.message || `Logo upload failed with status ${uploadRes.status}`);
        }
        if (uploadJson.uploadedBadges?.logo) {
          logoPath = String(uploadJson.uploadedBadges.logo);
        }
      }

      const questionsClean = questions
        .map((q) => {
          const word = cleanTopicLabel(q?.word);
          if (!word) return null;
          const entry = {
            word,
            answers: dedupeTopicAnswers(q?.answers),
            rejected_answers: dedupeTopicAnswers(q?.rejected_answers),
          };
          const didYouKnow = String(q?.did_you_know ?? "").trim();
          if (didYouKnow) entry.did_you_know = didYouKnow.slice(0, DID_YOU_KNOW_MAX);
          return entry;
        })
        .filter(Boolean);
      const topics = questionsClean.map((q) => q.word).filter(Boolean);
      const stageOut = {
        ...stage,
        type: cfg.type,
        max_questions: Math.min(DAY3_TOPIC_MAX, questionsClean.length),
        rules: rulesClean.slice(0, 6),
        questions: questionsClean,
        topics,
        words: topics,
        title: String(stage.title ?? "").trim(),
        logo: logoPath,
        timer: String(stage.timer ?? STAGE_DEFAULT_TIMER).trim() || STAGE_DEFAULT_TIMER,
      };

      const nextStages = { ...stagesData, stage_3: stageOut };
      await updateThemeData({
        payload: {
          data: { stages_data: nextStages },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });

      Swal.fire("Saved", "Stage 3 saved successfully.", "success");
      setLogoFile(null);
      setLogoPreview(null);
      dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to save Stage 3.", "error");
    } finally {
      setSavingDay(false);
    }
  };

  const handleSaveDay4All = async () => {
    if (!adminToken) {
      Swal.fire("Error", "Session expired. Please log in again.", "error");
      return;
    }
    if (!validateStageTimerValue(stage.timer, 4)) return;

    const rulesClean = (Array.isArray(stage.rules) ? stage.rules : [])
      .map((r) => String(r ?? "").trim())
      .filter(Boolean);
    if (rulesClean.length === 0) {
      Swal.fire({ title: "Validation", text: "Add at least one game rule.", icon: "error" });
      return;
    }
    if (String(stage.title ?? "").length > TITLE_MAX) {
      Swal.fire("Validation Error", `Title cannot exceed ${TITLE_MAX} characters.`, "error");
      return;
    }
    if (descriptionToTextareaMultiline(stage.description).length > DESCRIPTION_MAX) {
      Swal.fire("Validation Error", `Description cannot exceed ${DESCRIPTION_MAX} characters.`, "error");
      return;
    }

    try {
      setSavingDay(true);
      let logoPath = String(stage.logo ?? "").trim();

      if (logoFile) {
        const formData = new FormData();
        formData.append("logo", logoFile);
        formData.append("day", 4);
        formData.append("currentTheme", currentTheme);
        formData.append("organizationId", organizationId || "admin");
        formData.append("sessionId", sessionId || "admin");

        const uploadRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/uploadBadges`, {
          method: "POST",
          headers: { Authorization: `Bearer ${adminToken}` },
          body: formData,
        });
        const uploadJson = await uploadRes.json().catch(() => ({}));
        if (!uploadRes.ok) {
          throw new Error(uploadJson.message || `Logo upload failed with status ${uploadRes.status}`);
        }
        if (uploadJson.uploadedBadges?.logo) {
          logoPath = String(uploadJson.uploadedBadges.logo);
        }
      }

      const stageOut = {
        ...stage,
        type: cfg.type,
        rules: rulesClean.slice(0, 6),
        title: String(stage.title ?? "").trim(),
        description: String(stage.description ?? "").trim(),
        timer: String(stage.timer ?? STAGE_DEFAULT_TIMER).trim() || STAGE_DEFAULT_TIMER,
        logo: logoPath,
      };

      const nextStages = { ...stagesData, stage_4: stageOut };
      await updateThemeData({
        payload: {
          data: { stages_data: nextStages },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });

      Swal.fire("Saved", "Stage 4 saved successfully.", "success");
      setLogoFile(null);
      setLogoPreview(null);
      dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to save Stage 4.", "error");
    } finally {
      setSavingDay(false);
    }
  };

  const handleSaveDay5All = async () => {
    if (!adminToken) {
      Swal.fire("Error", "Session expired. Please log in again.", "error");
      return;
    }
    if (!validateStageTimerValue(stage.timer, 5)) return;

    const questions = (Array.isArray(questionsPairs) ? questionsPairs : []).filter((q) =>
      String(q?.word ?? "").trim()
    );
    if (!validatePairs(questions)) return;
    if (questions.length === 0) {
      Swal.fire("Validation", "Add at least one word.", "error");
      return;
    }
    if (questions.length > DAY1_WORD_MAX) {
      Swal.fire("Too Many Words", `Stage 5 allows a maximum of ${DAY1_WORD_MAX} words.`, "error");
      return;
    }

    const rulesClean = (Array.isArray(stage.rules) ? stage.rules : [])
      .map((r) => String(r ?? "").trim())
      .filter(Boolean);
    if (rulesClean.length === 0) {
      Swal.fire({ title: "Validation", text: "Add at least one game rule.", icon: "error" });
      return;
    }
    if (String(stage.title ?? "").length > TITLE_MAX) {
      Swal.fire("Validation Error", `Title cannot exceed ${TITLE_MAX} characters.`, "error");
      return;
    }

    try {
      setSavingDay(true);
      let logoPath = String(stage.logo ?? "").trim();

      if (logoFile) {
        const formData = new FormData();
        formData.append("logo", logoFile);
        formData.append("day", 5);
        formData.append("currentTheme", currentTheme);
        formData.append("organizationId", organizationId || "admin");
        formData.append("sessionId", sessionId || "admin");

        const uploadRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/uploadBadges`, {
          method: "POST",
          headers: { Authorization: `Bearer ${adminToken}` },
          body: formData,
        });
        const uploadJson = await uploadRes.json().catch(() => ({}));
        if (!uploadRes.ok) {
          throw new Error(uploadJson.message || `Logo upload failed with status ${uploadRes.status}`);
        }
        if (uploadJson.uploadedBadges?.logo) {
          logoPath = String(uploadJson.uploadedBadges.logo);
        }
      }

      const words = questions.map((q) => cleanWordleWord(q?.word)).filter(Boolean);
      const stageOut = {
        ...stage,
        type: cfg.type,
        max_questions: Math.min(DAY1_WORD_MAX, questions.length),
        rules: rulesClean.slice(0, 6),
        questions,
        words,
        title: String(stage.title ?? "").trim(),
        logo: logoPath,
        timer: String(stage.timer ?? STAGE_DEFAULT_TIMER).trim() || STAGE_DEFAULT_TIMER,
      };

      const nextStages = { ...stagesData, stage_5: stageOut };
      await updateThemeData({
        payload: {
          data: { stages_data: nextStages },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });

      Swal.fire("Saved", "Stage 5 saved successfully.", "success");
      setLogoFile(null);
      setLogoPreview(null);
      dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to save Stage 5.", "error");
    } finally {
      setSavingDay(false);
    }
  };

  const handleSaveDay6All = async () => {
    if (!adminToken) {
      Swal.fire("Error", "Session expired. Please log in again.", "error");
      return;
    }
    if (!validateStageTimerValue(stage.timer, 6)) return;

    const rulesClean = (Array.isArray(stage.rules) ? stage.rules : [])
      .map((r) => String(r ?? "").trim())
      .filter(Boolean);
    if (rulesClean.length === 0) {
      Swal.fire({ title: "Validation", text: "Add at least one game rule.", icon: "error" });
      return;
    }
    if (String(stage.title ?? "").length > TITLE_MAX) {
      Swal.fire("Validation Error", `Title cannot exceed ${TITLE_MAX} characters.`, "error");
      return;
    }
    if (descriptionToTextarea(stage.description).length > getDayDescriptionMax(6)) {
      Swal.fire("Validation Error", `Description cannot exceed ${getDayDescriptionMax(6)} characters.`, "error");
      return;
    }

    try {
      setSavingDay(true);
      let logoPath = String(stage.logo ?? "").trim();

      if (logoFile) {
        const formData = new FormData();
        formData.append("logo", logoFile);
        formData.append("day", 6);
        formData.append("currentTheme", currentTheme);
        formData.append("organizationId", organizationId || "admin");
        formData.append("sessionId", sessionId || "admin");

        const uploadRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/uploadBadges`, {
          method: "POST",
          headers: { Authorization: `Bearer ${adminToken}` },
          body: formData,
        });
        const uploadJson = await uploadRes.json().catch(() => ({}));
        if (!uploadRes.ok) {
          throw new Error(uploadJson.message || `Logo upload failed with status ${uploadRes.status}`);
        }
        if (uploadJson.uploadedBadges?.logo) {
          logoPath = String(uploadJson.uploadedBadges.logo);
        }
      }

      const stageOut = {
        ...stage,
        type: cfg.type,
        rules: rulesClean.slice(0, 6),
        title: String(stage.title ?? "").trim(),
        description: String(stage.description ?? "").trim(),
        timer: String(stage.timer ?? STAGE_DEFAULT_TIMER).trim() || STAGE_DEFAULT_TIMER,
        logo: logoPath,
      };

      const nextStages = { ...stagesData, stage_6: stageOut };
      await updateThemeData({
        payload: {
          data: { stages_data: nextStages },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });

      Swal.fire("Saved", "Stage 6 saved successfully.", "success");
      setLogoFile(null);
      setLogoPreview(null);
      dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to save Stage 6.", "error");
    } finally {
      setSavingDay(false);
    }
  };

  const handleSaveDay7All = async () => {
    if (!adminToken) {
      Swal.fire("Error", "Session expired. Please log in again.", "error");
      return;
    }
    if (!validateStageTimerValue(stage.timer, 7)) return;

    const rulesClean = (Array.isArray(stage.rules) ? stage.rules : [])
      .map((r) => String(r ?? "").trim())
      .filter(Boolean);
    if (rulesClean.length === 0) {
      Swal.fire({ title: "Validation", text: "Add at least one game rule.", icon: "error" });
      return;
    }
    if (String(stage.title ?? "").length > TITLE_MAX) {
      Swal.fire("Validation Error", `Title cannot exceed ${TITLE_MAX} characters.`, "error");
      return;
    }
    if (descriptionToTextareaMultiline(stage.description).length > getDayDescriptionMax(7)) {
      Swal.fire("Validation Error", `Description cannot exceed ${getDayDescriptionMax(7)} characters.`, "error");
      return;
    }

    try {
      setSavingDay(true);
      let logoPath = String(stage.logo ?? "").trim();

      if (logoFile) {
        const formData = new FormData();
        formData.append("logo", logoFile);
        formData.append("day", 7);
        formData.append("currentTheme", currentTheme);
        formData.append("organizationId", organizationId || "admin");
        formData.append("sessionId", sessionId || "admin");

        const uploadRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/uploadBadges`, {
          method: "POST",
          headers: { Authorization: `Bearer ${adminToken}` },
          body: formData,
        });
        const uploadJson = await uploadRes.json().catch(() => ({}));
        if (!uploadRes.ok) {
          throw new Error(uploadJson.message || `Logo upload failed with status ${uploadRes.status}`);
        }
        if (uploadJson.uploadedBadges?.logo) {
          logoPath = String(uploadJson.uploadedBadges.logo);
        }
      }

      const stageOut = {
        ...stage,
        type: cfg.type,
        rules: rulesClean.slice(0, 6),
        title: String(stage.title ?? "").trim(),
        description: String(stage.description ?? "").trim(),
        timer: String(stage.timer ?? STAGE_DEFAULT_TIMER).trim() || STAGE_DEFAULT_TIMER,
        logo: logoPath,
      };

      const nextStages = { ...stagesData, stage_7: stageOut };
      await updateThemeData({
        payload: {
          data: { stages_data: nextStages },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });

      Swal.fire("Saved", "Stage 7 saved successfully.", "success");
      setLogoFile(null);
      setLogoPreview(null);
      dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to save Stage 7.", "error");
    } finally {
      setSavingDay(false);
    }
  };

  const handleSaveDay8All = async () => {
    if (!adminToken) {
      Swal.fire("Error", "Session expired. Please log in again.", "error");
      return;
    }
    if (!validateStageTimerValue(stage.timer, 8)) return;

    const rulesClean = (Array.isArray(stage.rules) ? stage.rules : [])
      .map((r) => String(r ?? "").trim())
      .filter(Boolean);
    if (rulesClean.length === 0) {
      Swal.fire({ title: "Validation", text: "Add at least one game rule.", icon: "error" });
      return;
    }
    if (String(stage.title ?? "").length > TITLE_MAX) {
      Swal.fire("Validation Error", `Title cannot exceed ${TITLE_MAX} characters.`, "error");
      return;
    }
    if (descriptionToTextareaMultiline(stage.description).length > getDayDescriptionMax(8)) {
      Swal.fire("Validation Error", `Description cannot exceed ${getDayDescriptionMax(8)} characters.`, "error");
      return;
    }

    try {
      setSavingDay(true);
      let logoPath = String(stage.logo ?? "").trim();

      if (logoFile) {
        const formData = new FormData();
        formData.append("logo", logoFile);
        formData.append("day", 8);
        formData.append("currentTheme", currentTheme);
        formData.append("organizationId", organizationId || "admin");
        formData.append("sessionId", sessionId || "admin");

        const uploadRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/uploadBadges`, {
          method: "POST",
          headers: { Authorization: `Bearer ${adminToken}` },
          body: formData,
        });
        const uploadJson = await uploadRes.json().catch(() => ({}));
        if (!uploadRes.ok) {
          throw new Error(uploadJson.message || `Logo upload failed with status ${uploadRes.status}`);
        }
        if (uploadJson.uploadedBadges?.logo) {
          logoPath = String(uploadJson.uploadedBadges.logo);
        }
      }

      const stageOut = {
        ...stage,
        type: cfg.type,
        rules: rulesClean.slice(0, 6),
        title: String(stage.title ?? "").trim(),
        description: String(stage.description ?? "").trim(),
        timer: String(stage.timer ?? STAGE_DEFAULT_TIMER).trim() || STAGE_DEFAULT_TIMER,
        logo: logoPath,
      };

      const nextStages = { ...stagesData, stage_8: stageOut };
      await updateThemeData({
        payload: {
          data: { stages_data: nextStages },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });

      Swal.fire("Saved", "Stage 8 saved successfully.", "success");
      setLogoFile(null);
      setLogoPreview(null);
      dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to save Stage 8.", "error");
    } finally {
      setSavingDay(false);
    }
  };

  const handleSaveDay9All = async () => {
    if (!adminToken) {
      Swal.fire("Error", "Session expired. Please log in again.", "error");
      return;
    }
    if (!validateStageTimerValue(stage.timer, 9)) return;

    const rulesClean = (Array.isArray(stage.rules) ? stage.rules : [])
      .map((r) => String(r ?? "").trim())
      .filter(Boolean);
    if (rulesClean.length === 0) {
      Swal.fire({ title: "Validation", text: "Add at least one game rule.", icon: "error" });
      return;
    }
    if (String(stage.title ?? "").length > TITLE_MAX) {
      Swal.fire("Validation Error", `Title cannot exceed ${TITLE_MAX} characters.`, "error");
      return;
    }
    if (descriptionToTextareaMultiline(stage.description).length > getDayDescriptionMax(9)) {
      Swal.fire("Validation Error", `Description cannot exceed ${getDayDescriptionMax(9)} characters.`, "error");
      return;
    }

    try {
      setSavingDay(true);
      let logoPath = String(stage.logo ?? "").trim();

      if (logoFile) {
        const formData = new FormData();
        formData.append("logo", logoFile);
        formData.append("day", 9);
        formData.append("currentTheme", currentTheme);
        formData.append("organizationId", organizationId || "admin");
        formData.append("sessionId", sessionId || "admin");

        const uploadRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/uploadBadges`, {
          method: "POST",
          headers: { Authorization: `Bearer ${adminToken}` },
          body: formData,
        });
        const uploadJson = await uploadRes.json().catch(() => ({}));
        if (!uploadRes.ok) {
          throw new Error(uploadJson.message || `Logo upload failed with status ${uploadRes.status}`);
        }
        if (uploadJson.uploadedBadges?.logo) {
          logoPath = String(uploadJson.uploadedBadges.logo);
        }
      }

      const stageOut = {
        ...stage,
        type: cfg.type,
        rules: rulesClean.slice(0, 6),
        title: String(stage.title ?? "").trim(),
        description: String(stage.description ?? "").trim(),
        timer: String(stage.timer ?? STAGE_DEFAULT_TIMER).trim() || STAGE_DEFAULT_TIMER,
        logo: logoPath,
      };

      const nextStages = { ...stagesData, stage_9: stageOut };
      await updateThemeData({
        payload: {
          data: { stages_data: nextStages },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });

      Swal.fire("Saved", "Stage 9 saved successfully.", "success");
      setLogoFile(null);
      setLogoPreview(null);
      dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to save Stage 9.", "error");
    } finally {
      setSavingDay(false);
    }
  };

  const handleSaveDay10All = async () => {
    if (!adminToken) {
      Swal.fire("Error", "Session expired. Please log in again.", "error");
      return;
    }
    if (!validateStageTimerValue(stage.timer, 10)) return;

    const rulesClean = (Array.isArray(stage.rules) ? stage.rules : [])
      .map((r) => String(r ?? "").trim())
      .filter(Boolean);
    if (rulesClean.length === 0) {
      Swal.fire({ title: "Validation", text: "Add at least one game rule.", icon: "error" });
      return;
    }
    if (String(stage.title ?? "").length > TITLE_MAX) {
      Swal.fire("Validation Error", `Title cannot exceed ${TITLE_MAX} characters.`, "error");
      return;
    }
    if (descriptionToTextareaMultiline(stage.description).length > getDayDescriptionMax(10)) {
      Swal.fire("Validation Error", `Description cannot exceed ${getDayDescriptionMax(10)} characters.`, "error");
      return;
    }

    try {
      setSavingDay(true);
      let logoPath = String(stage.logo ?? "").trim();

      if (logoFile) {
        const formData = new FormData();
        formData.append("logo", logoFile);
        formData.append("day", 10);
        formData.append("currentTheme", currentTheme);
        formData.append("organizationId", organizationId || "admin");
        formData.append("sessionId", sessionId || "admin");

        const uploadRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/uploadBadges`, {
          method: "POST",
          headers: { Authorization: `Bearer ${adminToken}` },
          body: formData,
        });
        const uploadJson = await uploadRes.json().catch(() => ({}));
        if (!uploadRes.ok) {
          throw new Error(uploadJson.message || `Logo upload failed with status ${uploadRes.status}`);
        }
        if (uploadJson.uploadedBadges?.logo) {
          logoPath = String(uploadJson.uploadedBadges.logo);
        }
      }

      const stageOut = {
        ...stage,
        type: cfg.type,
        rules: rulesClean.slice(0, 6),
        title: String(stage.title ?? "").trim(),
        description: String(stage.description ?? "").trim(),
        timer: String(stage.timer ?? STAGE_DEFAULT_TIMER).trim() || STAGE_DEFAULT_TIMER,
        logo: logoPath,
      };

      const nextStages = { ...stagesData, stage_10: stageOut };
      await updateThemeData({
        payload: {
          data: { stages_data: nextStages },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });

      Swal.fire("Saved", "Stage 10 saved successfully.", "success");
      setLogoFile(null);
      setLogoPreview(null);
      dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to save Stage 10.", "error");
    } finally {
      setSavingDay(false);
    }
  };

  const handleSavePuzzle = async () => {
    if (!adminToken) {
      Swal.fire("Error", "Session expired. Please log in again.", "error");
      return;
    }

    const hasMainUpload = Boolean(puzzleMainFile) || Boolean(puzzleMainPreview);
    const hasSavedImage = Boolean(savedPuzzleImageFilename) || Boolean(resolvedPuzzleMainSrc);

    if (!hasMainUpload && !hasSavedImage) {
      Swal.fire("Image required", "Upload a Big Picture image before saving.", "info");
      return;
    }

    const partsToSave = clampPuzzleParts(puzzleParts);
    if (partsToSave !== puzzleParts) {
      setPuzzleParts(partsToSave);
      setPuzzlePartsInput(String(partsToSave));
    }

    let objectUrlToRevoke = null;

    try {
      setSavingPuzzle(true);

      const imageSrc = await resolveImageSourceForSlicing(
        puzzleMainFile,
        puzzleMainPreview,
        savedPuzzleMainObjectUrl,
        { assetFilename: savedPuzzleImageFilename, adminToken }
      );
      if (!imageSrc) {
        throw new Error("No puzzle image available to save.");
      }
      if (
        imageSrc.startsWith("blob:") &&
        imageSrc !== puzzleMainPreview &&
        imageSrc !== savedPuzzleMainObjectUrl
      ) {
        objectUrlToRevoke = imageSrc;
      }

      const { files: partFiles, dataUrls, rows: gridRows, cols: gridCols } = await sliceImageIntoPieces(
        imageSrc,
        partsToSave
      );
      setPuzzlePiecePreviews(dataUrls);
      setPuzzlePiecePreviewParts(partsToSave);
      const formData = new FormData();

      if (puzzleMainFile) {
        formData.append("puzzle_image", puzzleMainFile);
      } else {
        const mainBlob = await fetch(imageSrc).then((r) => r.blob());
        const ext = mainBlob.type.includes("png") ? "png" : "jpg";
        formData.append(
          "puzzle_image",
          new File([mainBlob], `puzzle_image.${ext}`, { type: mainBlob.type || "image/jpeg" })
        );
      }

      partFiles.forEach((file, i) => {
        formData.append(`puzzle_part_${i + 1}`, file);
      });

      formData.append("total_puzzles", String(partsToSave));
      formData.append("puzzle_rows", String(gridRows));
      formData.append("puzzle_cols", String(gridCols));
      formData.append("day", "puzzle");
      formData.append("currentTheme", currentTheme);
      formData.append("organizationId", organizationId || "admin");
      formData.append("sessionId", sessionId || "admin");

      const uploadRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/uploadBadges`, {
        method: "POST",
        headers: { Authorization: `Bearer ${adminToken}` },
        body: formData,
      });
      const uploadJson = await uploadRes.json().catch(() => ({}));
      if (!uploadRes.ok) {
        throw new Error(uploadJson.message || `Puzzle upload failed with status ${uploadRes.status}`);
      }

      Swal.fire("Saved", "Big Picture cut into pieces and saved successfully.", "success");
      setPuzzleMainFile(null);
      setPuzzleMainPreview(null);
      setPuzzlePartsBaseline(partsToSave);
      setPuzzlePiecePreviews(dataUrls);
      setPuzzlePiecePreviewParts(partsToSave);
      if (puzzleMainInputRef.current) {
        puzzleMainInputRef.current.value = "";
      }
      dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to save puzzle settings.", "error");
    } finally {
      if (objectUrlToRevoke) URL.revokeObjectURL(objectUrlToRevoke);
      setSavingPuzzle(false);
    }
  };

  const handlePuzzleMainChange = handleImageChange(
    setPuzzleMainFile,
    (preview) => {
      setPuzzleMainPreview(preview);
      setPuzzlePiecePreviews([]);
      setPuzzlePiecePreviewParts(null);
    },
    {
      ...PUZZLE_IMAGE_DIMENSIONS,
      allowJpeg: true,
      maxBytes: PUZZLE_IMAGE_MAX_BYTES,
      label: "Puzzle image",
    }
  );

  const handleDay1ExcelImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const ext = file.name.split(".").pop().toLowerCase();
    try {
      let rawRows = [];
      if (ext !== "csv") {
        Swal.fire("Invalid file", "Upload a .csv file.", "warning");
        return;
      }
      rawRows = parseCsvWordRows(await file.text());

      const { rows: imported, tooLongWords } = normalizeWordleRows(rawRows);

      if (tooLongWords.length) {
        const preview = tooLongWords.slice(0, 5).join(", ");
        const extra = tooLongWords.length > 5 ? ` (+${tooLongWords.length - 5} more)` : "";
        await Swal.fire({
          icon: "warning",
          title: "Words too long",
          text: `Each word must be at most ${DAY1_WORD_CHAR_MAX} characters. Skipped: ${preview}${extra}`,
        });
      }

      if (!imported.length) {
        if (!tooLongWords.length) {
          Swal.fire(
            "No words found",
            `Add words (max ${DAY1_WORD_CHAR_MAX} characters, ${DAY1_WORD_ALLOWED_HINT}) in column A.`,
            "warning"
          );
        }
        return;
      }

      const currentCount = Array.isArray(questionsPairs) ? questionsPairs.length : 0;
      if (currentCount > 0) {
        const { isConfirmed } = await Swal.fire({
          title: "Replace existing words?",
          text: `Import ${imported.length} word(s) from file. This will replace your current ${currentCount} word(s).`,
          icon: "question",
          showCancelButton: true,
          confirmButtonText: "Replace",
          cancelButtonText: "Cancel",
        });
        if (!isConfirmed) return;
      }

      setQuestionsPairs(imported);
      updateStage({ max_questions: Math.min(DAY1_WORD_MAX, imported.length) });
      Swal.fire("Imported", `${imported.length} word(s) loaded. Review and save when ready.`, "success");
    } catch (err) {
      Swal.fire("Import failed", err?.message || "Could not read the file.", "error");
    }
  };

  const handleDay3ExcelImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const ext = file.name.split(".").pop().toLowerCase();
    try {
      let rawRows = [];
      if (ext !== "csv") {
        Swal.fire("Invalid file", "Upload a .csv file.", "warning");
        return;
      }
      rawRows = parseCsvCellRows(await file.text());

      const { rows: imported, tooLongWords, excessWordCount } = normalizeTopicRows(rawRows);

      if (tooLongWords.length) {
        const preview = tooLongWords.slice(0, 5).join(", ");
        const extra = tooLongWords.length > 5 ? ` (+${tooLongWords.length - 5} more)` : "";
        await Swal.fire({
          icon: "warning",
          title: "Words too long",
          text: `Each word must be at most ${DAY3_TOPIC_CHAR_MAX} characters. Skipped: ${preview}${extra}`,
        });
      }

      if (excessWordCount > 0) {
        await Swal.fire({
          icon: "warning",
          title: "Word limit exceeded",
          text: `Stage 3 allows a maximum of ${DAY3_TOPIC_MAX} words. ${excessWordCount} extra row(s) in the CSV were skipped.`,
        });
      }

      if (!imported.length) {
        if (!tooLongWords.length && !excessWordCount) {
          Swal.fire(
            "No words found",
            `Add words (max ${DAY3_TOPIC_CHAR_MAX} characters, max ${DAY3_TOPIC_MAX} words) in column A.`,
            "warning"
          );
        }
        return;
      }

      const currentCount = Array.isArray(questionsPairs) ? questionsPairs.length : 0;
      if (currentCount > 0) {
        const { isConfirmed } = await Swal.fire({
          title: "Replace existing words?",
          text: `Import ${imported.length} word(s) from file. This will replace your current ${currentCount} word(s).`,
          icon: "question",
          showCancelButton: true,
          confirmButtonText: "Replace",
          cancelButtonText: "Cancel",
        });
        if (!isConfirmed) return;
      }

      setQuestionsPairs(imported);
      updateStage({ max_questions: Math.min(DAY3_TOPIC_MAX, imported.length) });
      Swal.fire("Imported", `${imported.length} word(s) loaded. Review and save when ready.`, "success");
    } catch (err) {
      Swal.fire("Import failed", err?.message || "Could not read the file.", "error");
    }
  };

  const handleSaveRulesAndQuestions = async () => {
    if (!adminToken) {
      Swal.fire("Error", "Session expired. Please log in again.", "error");
      return;
    }

    if (activeDay !== 1 && String(stage.streak_message ?? "").length > STREAK_MESSAGE_MAX) {
      Swal.fire("Validation Error", `Streak Message cannot exceed ${STREAK_MESSAGE_MAX} characters.`, "error");
      return;
    }
    if (activeDay !== 1 && String(stage.title ?? "").length > TITLE_MAX) {
      Swal.fire("Validation Error", `Title cannot exceed ${TITLE_MAX} characters.`, "error");
      return;
    }
    try {
      setSavingRulesAndQuestions(true);
      const rulesClean = (stage.rules || []).map((r) => String(r ?? "").trim()).filter(Boolean).slice(0, 6);
      if (rulesClean.length === 0) {
        Swal.fire("Validation", "Add at least one game rule.", "error");
        setSavingRulesAndQuestions(false);
        return;
      }

      const stageOut = {
        ...stage,
        rules: rulesClean,
        max_questions:
          stage.max_questions != null && String(stage.max_questions).trim() !== ""
            ? Math.min(cfg.maxQ, Math.max(1, Math.floor(Number(stage.max_questions) || 1)))
            : cfg.maxQ,
        streak_message: String(stage.streak_message ?? "").trim(),
        title: String(stage.title ?? "").trim(),
        logo: String(stage.logo ?? "").trim(),
      };

      const nextStages = { ...stagesData, [stageKey]: stageOut };
      const payload = {
        data: { stages_data: nextStages },
        organizationId: organizationId || "admin",
        sessionId: sessionId || "admin",
        currentTheme,
      };
      await updateThemeData({ payload, token: adminToken });
      Swal.fire("Saved", `Stage ${activeDay} rules and questions saved successfully.`, "success");
      dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to save rules and questions.", "error");
    } finally {
      setSavingRulesAndQuestions(false);
    }
  };

  if (status === "loading" && !data) {
    return (
      <div className="themeupdate-page">
        <div className="themeupdate-loading">Loading...</div>
      </div>
    );
  }

  const currentPairCount = Array.isArray(questionsPairs) ? questionsPairs.length : 0;
  const dayWordListCount = (Array.isArray(questionsPairs) ? questionsPairs : []).filter((q) =>
    getDayWordLabel(q)
  ).length;
  const atMaxPairs = currentPairCount >= effectiveMaxQuestions;

  return (
    <div className="themeupdate-page">
      <div className="back-button-holder">
        <button type="button" className="back-button" onClick={() => navigate("/admin/rules")}>
          <i className="fa-solid fa-arrow-left"></i> Back
        </button>
      </div>

      <nav className="themeupdate-tabs" aria-label="Tabs">
        {dayTabs.map((d) => (
          <button
            key={d}
            type="button"
            className={`themeupdate-tab ${!activePuzzleTab && activeDay === d ? "themeupdate-tab--active" : ""}`}
            onClick={() => {
              setActivePuzzleTab(false);
              setActiveDay(d);
            }}
          >
            Stage {d}
            <span
              style={{ display: "block", fontSize: 11, fontWeight: 400, opacity: 0.75, marginTop: 2 }}
            >
              {STAGE_GAME_NAMES[d]}
            </span>
          </button>
        ))}
        <button
          type="button"
          className={`themeupdate-tab ${activePuzzleTab ? "themeupdate-tab--active" : ""}`}
          onClick={() => setActivePuzzleTab(true)}
        >
          Puzzle
        </button>
      </nav>

      <div className="themeupdate-panel">
        {!activePuzzleTab && activeDay === 5 ? (
          <div className="themeupdate-card">
            <div className="themeupdate-card-header">Stage 5 — Wordle</div>
            <div className="themeupdate-card-body">
              <div className="themeupdate-badge-upload-box" style={{ marginBottom: 20 }}>
                <label className="themeupdate-label">Logo</label>
                <input
                  type="file"
                  accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                  onChange={handleImageChange(setLogoFile, setLogoPreview, {
                    ...LOGO_DIMENSIONS,
                    label: "Logo",
                    allowJpeg: true,
                  })}
                />
                <div className="themeupdate-help">
                  <span>* PNG or JPEG, {LOGO_DIMENSIONS.width}×{LOGO_DIMENSIONS.height} px</span>
                </div>
                {(logoPreview || savedLogoUrl) && (
                  <div style={{ marginTop: 10 }}>
                    <div className="themeupdate-help" style={{ color: "#6c757d" }}>
                      <span>{logoPreview ? "Selected image" : "Saved image"}</span>
                    </div>
                    <img
                      src={logoPreview || savedLogoUrl}
                      alt="Stage 5 logo"
                      className="themeupdate-preview-img"
                    />
                  </div>
                )}
              </div>

              <div className="themeupdate-total-questions" style={{ marginBottom: 18, borderTop: "none", paddingTop: 0 }}>
                <label className="themeupdate-label">
                  Title
                  <span style={{ fontWeight: 400, fontSize: 13, color: "#6c757d", marginLeft: 8 }}>
                    (Max {TITLE_MAX} chars)
                  </span>
                </label>
                <input
                  type="text"
                  className="themeupdate-input"
                  value={stage.title ?? ""}
                  maxLength={TITLE_MAX}
                  onChange={(e) => {
                    const val = clampWithWarn(e.target.value, TITLE_MAX, "Title");
                    updateStage({ title: val });
                  }}
                  placeholder="Stage title"
                />
                <CharCount value={stage.title} max={TITLE_MAX} />
              </div>

              <div className="themeupdate-rules-section">
                <label className="themeupdate-label">Rules</label>
                <div className="themeupdate-rules-repeater">
                  {(Array.isArray(stage.rules) ? stage.rules : [""]).map((r, idx) => (
                    <div key={idx}>
                      <div className="themeupdate-rule-row">
                        <input
                          type="text"
                          className="themeupdate-input themeupdate-rule-input"
                          value={r ?? ""}
                          onChange={(e) => updateRuleCell(idx, e.target.value)}
                          placeholder={`Rule ${idx + 1}`}
                          maxLength={200}
                        />
                        <button
                          type="button"
                          className="themeupdate-btn themeupdate-btn-sm themeupdate-btn-danger"
                          onClick={() => removeRule(idx)}
                          disabled={(stage.rules?.length ?? 1) <= 1}
                          aria-label="Remove rule"
                        >
                          −
                        </button>
                      </div>
                      <CharCount value={r} max={200} />
                    </div>
                  ))}
                  <button
                    type="button"
                    className="themeupdate-btn themeupdate-btn-sm themeupdate-btn-primary themeupdate-add-rule"
                    onClick={addRule}
                    disabled={(stage.rules?.length ?? 1) >= 6}
                  >
                    + Add rule
                  </button>
                  <div className="themeupdate-help" style={{ marginTop: 6 }}>
                    <span>Max 6 rules. At least 1 required. 200 characters per rule.</span>
                  </div>
                </div>
              </div>

              <StageTimerInput stage={stage} updateStage={updateStage} />

              <div className="themeupdate-rules-section">
                <label className="themeupdate-label">
                  Words
                  <span style={{ fontWeight: 400, fontSize: 13, color: "#6c757d", marginLeft: 8 }}>
                    ({dayWordListCount}/{DAY1_WORD_MAX})
                  </span>
                </label>
                <div className="themeupdate-word-tags" role="list">
                  {(Array.isArray(questionsPairs) ? questionsPairs : []).map((q, idx) => {
                    const word = cleanWordleWord(q?.word);
                    if (!word) return null;
                    const type = normalizeDay1WordType(q?.type ?? q?.kind ?? q?.word_type ?? q?.wordType ?? "");
                    return (
                      <span key={`${word}-${idx}`} className="themeupdate-word-tag" role="listitem">
                        <span style={{ fontSize: 11, fontWeight: 700, opacity: 0.75, marginRight: 6 }}>
                          {type === "bad" ? "Bad" : "Good"}
                        </span>
                        {word}
                        <button
                          type="button"
                          className="themeupdate-word-tag-remove"
                          onClick={() => deletePair(idx)}
                          aria-label={`Remove ${word}`}
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>

                <div className="themeupdate-day1-add-word">
                  <select
                    className="themeupdate-input"
                    value={day1WordType}
                    onChange={(e) => setDay1WordType(e.target.value)}
                    style={{ width: 120, marginRight: 8 }}
                    aria-label="Word type"
                  >
                    <option value="good">Good</option>
                    <option value="bad">Bad</option>
                  </select>
                  <input
                    type="text"
                    className="themeupdate-input"
                    value={day1WordInput}
                    maxLength={cfg.questionMax}
                    placeholder={`Type a word (max ${DAY1_WORD_CHAR_MAX} characters)`}
                    onChange={(e) => {
                      const cleaned = String(e.target.value ?? "")
                        .replace(/[^a-zA-Z\s-]/g, "")
                        .replace(/\s+/g, " ")
                        .slice(0, DAY1_WORD_CHAR_MAX);
                      setDay1WordInput(cleaned);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddDay1Word();
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="themeupdate-btn themeupdate-btn-primary themeupdate-btn-sm"
                    onClick={handleAddDay1Word}
                    disabled={dayWordListCount >= DAY1_WORD_MAX}
                  >
                    Add
                  </button>
                </div>

                <input
                  ref={day1ExcelInputRef}
                  type="file"
                  accept=".csv"
                  style={{ display: "none" }}
                  onChange={handleDay1ExcelImport}
                />
                <div className="themeupdate-day1-import-actions">
                  <button
                    type="button"
                    className="themeupdate-btn themeupdate-btn-neutral-outline themeupdate-btn-sm"
                    onClick={() => day1ExcelInputRef.current?.click()}
                  >
                    Upload CSV
                  </button>
                  <button
                    type="button"
                    className="themeupdate-btn themeupdate-btn-sm themeupdate-btn-neutral-outline"
                    onClick={downloadDay1SampleCsv}
                  >
                    Sample CSV
                  </button>
                </div>
                <div className="themeupdate-help" style={{ marginTop: 6 }}>
                  <span>
                    One word per row (max {DAY1_WORD_CHAR_MAX} characters, {DAY1_WORD_ALLOWED_HINT}). Max{" "}
                    {DAY1_WORD_MAX} words. Use quotes in CSV for words with commas.
                  </span>
                </div>
              </div>

              <div className="themeupdate-actions" style={{ justifyContent: "center", marginTop: 28 }}>
                <button
                  type="button"
                  className="themeupdate-btn themeupdate-btn-success"
                  onClick={handleSaveDay5All}
                  disabled={savingDay}
                >
                  {savingDay ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        ) : !activePuzzleTab && activeDay === 2 ? (
          <div className="themeupdate-card">
            <div className="themeupdate-card-header">Stage 2 — Comic Strip Chaos</div>
            <div className="themeupdate-card-body">
              <div className="themeupdate-badge-upload-box" style={{ marginBottom: 20 }}>
                <label className="themeupdate-label">Logo</label>
                <input
                  type="file"
                  accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                  onChange={handleImageChange(setLogoFile, setLogoPreview, {
                    ...LOGO_DIMENSIONS,
                    label: "Logo",
                    allowJpeg: true,
                  })}
                />
                <div className="themeupdate-help">
                  <span>* PNG or JPEG, {LOGO_DIMENSIONS.width}×{LOGO_DIMENSIONS.height} px</span>
                </div>
                {(logoPreview || savedLogoUrl) && (
                  <div style={{ marginTop: 10 }}>
                    <div className="themeupdate-help" style={{ color: "#6c757d" }}>
                      <span>{logoPreview ? "Selected image" : "Saved image"}</span>
                    </div>
                    <img
                      src={logoPreview || savedLogoUrl}
                      alt="Stage 2 logo"
                      className="themeupdate-preview-img"
                    />
                  </div>
                )}
              </div>

              <div className="themeupdate-total-questions" style={{ marginBottom: 18, borderTop: "none", paddingTop: 0 }}>
                <label className="themeupdate-label">
                  Title
                  <span style={{ fontWeight: 400, fontSize: 13, color: "#6c757d", marginLeft: 8 }}>
                    (Max {TITLE_MAX} chars)
                  </span>
                </label>
                <input
                  type="text"
                  className="themeupdate-input"
                  value={stage.title ?? ""}
                  maxLength={TITLE_MAX}
                  onChange={(e) => {
                    const val = clampWithWarn(e.target.value, TITLE_MAX, "Title");
                    updateStage({ title: val });
                  }}
                  placeholder="Stage title"
                />
                <CharCount value={stage.title} max={TITLE_MAX} />
              </div>

              <div className="themeupdate-rules-section">
                <label className="themeupdate-label">Rules</label>
                <div className="themeupdate-rules-repeater">
                  {(Array.isArray(stage.rules) ? stage.rules : [""]).map((r, idx) => (
                    <div key={idx}>
                      <div className="themeupdate-rule-row">
                        <input
                          type="text"
                          className="themeupdate-input themeupdate-rule-input"
                          value={r ?? ""}
                          onChange={(e) => updateRuleCell(idx, e.target.value)}
                          placeholder={`Rule ${idx + 1}`}
                          maxLength={200}
                        />
                        <button
                          type="button"
                          className="themeupdate-btn themeupdate-btn-sm themeupdate-btn-danger"
                          onClick={() => removeRule(idx)}
                          disabled={(stage.rules?.length ?? 1) <= 1}
                          aria-label="Remove rule"
                        >
                          −
                        </button>
                      </div>
                      <CharCount value={r} max={200} />
                    </div>
                  ))}
                  <button
                    type="button"
                    className="themeupdate-btn themeupdate-btn-sm themeupdate-btn-primary themeupdate-add-rule"
                    onClick={addRule}
                    disabled={(stage.rules?.length ?? 1) >= 6}
                  >
                    + Add rule
                  </button>
                  <div className="themeupdate-help" style={{ marginTop: 6 }}>
                    <span>Max 6 rules. At least 1 required. 200 characters per rule.</span>
                  </div>
                </div>
              </div>

              <StageTimerInput stage={stage} updateStage={updateStage} />

              <div className="themeupdate-total-questions">
                <label className="themeupdate-label">
                  Description
                  <span style={{ fontWeight: 400, fontSize: 13, color: "#6c757d", marginLeft: 8 }}>
                    (Max {getDayDescriptionMax(2)} chars)
                  </span>
                </label>
                <textarea
                  className="themeupdate-input themeupdate-textarea"
                  value={descriptionToTextareaMultiline(stage.description)}
                  maxLength={getDayDescriptionMax(2)}
                  rows={6}
                  onChange={(e) => {
                    const val = clampWithWarn(e.target.value, getDayDescriptionMax(2), "Description");
                    updateStage({ description: descriptionFromTextareaMultiline(val) });
                  }}
                  placeholder={`e.g. ${STAGE_DESCRIPTION_EXAMPLES[2]}`}
                />
                <CharCount value={descriptionToTextareaMultiline(stage.description)} max={getDayDescriptionMax(2)} />
              </div>

              <div className="themeupdate-actions" style={{ justifyContent: "center", marginTop: 28 }}>
                <button
                  type="button"
                  className="themeupdate-btn themeupdate-btn-success"
                  onClick={handleSaveDay2All}
                  disabled={savingDay}
                >
                  {savingDay ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        ) : !activePuzzleTab && activeDay === 3 ? (
          <div className="themeupdate-card">
            <div className="themeupdate-card-header">Stage 3 — Category Rush</div>
            <div className="themeupdate-card-body">
              <div className="themeupdate-badge-upload-box" style={{ marginBottom: 20 }}>
                <label className="themeupdate-label">Logo</label>
                <input
                  type="file"
                  accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                  onChange={handleImageChange(setLogoFile, setLogoPreview, {
                    ...LOGO_DIMENSIONS,
                    label: "Logo",
                    allowJpeg: true,
                  })}
                />
                <div className="themeupdate-help">
                  <span>* PNG or JPEG, {LOGO_DIMENSIONS.width}×{LOGO_DIMENSIONS.height} px</span>
                </div>
                {(logoPreview || savedLogoUrl) && (
                  <div style={{ marginTop: 10 }}>
                    <div className="themeupdate-help" style={{ color: "#6c757d" }}>
                      <span>{logoPreview ? "Selected image" : "Saved image"}</span>
                    </div>
                    <img
                      src={logoPreview || savedLogoUrl}
                      alt="Stage 3 logo"
                      className="themeupdate-preview-img"
                    />
                  </div>
                )}
              </div>

              <div className="themeupdate-total-questions" style={{ marginBottom: 18, borderTop: "none", paddingTop: 0 }}>
                <label className="themeupdate-label">
                  Title
                  <span style={{ fontWeight: 400, fontSize: 13, color: "#6c757d", marginLeft: 8 }}>
                    (Max {TITLE_MAX} chars)
                  </span>
                </label>
                <input
                  type="text"
                  className="themeupdate-input"
                  value={stage.title ?? ""}
                  maxLength={TITLE_MAX}
                  onChange={(e) => {
                    const val = clampWithWarn(e.target.value, TITLE_MAX, "Title");
                    updateStage({ title: val });
                  }}
                  placeholder="Stage title"
                />
                <CharCount value={stage.title} max={TITLE_MAX} />
              </div>

              <div className="themeupdate-rules-section">
                <label className="themeupdate-label">Rules</label>
                <div className="themeupdate-rules-repeater">
                  {(Array.isArray(stage.rules) ? stage.rules : [""]).map((r, idx) => (
                    <div key={idx}>
                      <div className="themeupdate-rule-row">
                        <input
                          type="text"
                          className="themeupdate-input themeupdate-rule-input"
                          value={r ?? ""}
                          onChange={(e) => updateRuleCell(idx, e.target.value)}
                          placeholder={`Rule ${idx + 1}`}
                          maxLength={200}
                        />
                        <button
                          type="button"
                          className="themeupdate-btn themeupdate-btn-sm themeupdate-btn-danger"
                          onClick={() => removeRule(idx)}
                          disabled={(stage.rules?.length ?? 1) <= 1}
                          aria-label="Remove rule"
                        >
                          −
                        </button>
                      </div>
                      <CharCount value={r} max={200} />
                    </div>
                  ))}
                  <button
                    type="button"
                    className="themeupdate-btn themeupdate-btn-sm themeupdate-btn-primary themeupdate-add-rule"
                    onClick={addRule}
                    disabled={(stage.rules?.length ?? 1) >= 6}
                  >
                    + Add rule
                  </button>
                  <div className="themeupdate-help" style={{ marginTop: 6 }}>
                    <span>Max 6 rules. At least 1 required. 200 characters per rule.</span>
                  </div>
                </div>
              </div>

              <StageTimerInput stage={stage} updateStage={updateStage} />

              <div className="themeupdate-rules-section">
                <label className="themeupdate-label">
                  Words
                  <span style={{ fontWeight: 400, fontSize: 13, color: "#6c757d", marginLeft: 8 }}>
                    ({dayWordListCount}/{DAY3_TOPIC_MAX})
                  </span>
                </label>
                <div className="themeupdate-help" style={{ marginTop: -4, marginBottom: 10 }}>
                  <span>
                    For each topic set the answers players may enter. Add <strong>acceptable</strong>{" "}
                    answers to allow only those, and <strong>not acceptable</strong> answers to block
                    specific words.
                  </span>
                </div>
                <div className="themeupdate-topic-card-list">
                  {(Array.isArray(questionsPairs) ? questionsPairs : []).map((q, idx) => {
                    const word = cleanTopicLabel(q?.word);
                    if (!word) return null;
                    return (
                      <Day3TopicCard
                        key={`${word}-${idx}`}
                        topic={q}
                        index={idx}
                        disabled={savingDay}
                        onRemoveTopic={deletePair}
                        onAddAnswer={addDay3TopicAnswer}
                        onRemoveAnswer={removeDay3TopicAnswer}
                      />
                    );
                  })}
                </div>

                <div className="themeupdate-day1-add-word">
                  <input
                    type="text"
                    className="themeupdate-input"
                    value={day1WordInput}
                    maxLength={DAY3_TOPIC_CHAR_MAX}
                    placeholder={`Type a word (max ${DAY3_TOPIC_CHAR_MAX} characters)`}
                    onChange={(e) => {
                      const cleaned = String(e.target.value ?? "")
                        .replace(/\s+/g, " ")
                        .slice(0, DAY3_TOPIC_CHAR_MAX);
                      setDay1WordInput(cleaned);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddDay3Word();
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="themeupdate-btn themeupdate-btn-primary themeupdate-btn-sm"
                    onClick={handleAddDay3Word}
                    disabled={dayWordListCount >= DAY3_TOPIC_MAX}
                  >
                    Add
                  </button>
                </div>

                <input
                  ref={day3ExcelInputRef}
                  type="file"
                  accept=".csv"
                  style={{ display: "none" }}
                  onChange={handleDay3ExcelImport}
                />
                <div className="themeupdate-day1-import-actions">
                  <button
                    type="button"
                    className="themeupdate-btn themeupdate-btn-neutral-outline themeupdate-btn-sm"
                    onClick={() => day3ExcelInputRef.current?.click()}
                  >
                    Upload CSV
                  </button>
                  <button
                    type="button"
                    className="themeupdate-btn themeupdate-btn-sm themeupdate-btn-neutral-outline"
                    onClick={downloadDay3SampleCsv}
                  >
                    Sample CSV
                  </button>
                </div>
                <div className="themeupdate-help" style={{ marginTop: 6 }}>
                  <span>
                    CSV columns: <strong>Word, Acceptable Answers, Not Acceptable Answers</strong>.
                    Separate multiple answers in a cell with <strong>|</strong> (e.g.{" "}
                    <code>Dune|Star Wars</code>). Max {DAY3_TOPIC_MAX} topics, {DAY3_TOPIC_CHAR_MAX}{" "}
                    characters per word. Download the sample for the exact format.
                  </span>
                </div>
              </div>

              <div className="themeupdate-actions" style={{ justifyContent: "center", marginTop: 28 }}>
                <button
                  type="button"
                  className="themeupdate-btn themeupdate-btn-success"
                  onClick={handleSaveDay3All}
                  disabled={savingDay}
                >
                  {savingDay ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        ) : !activePuzzleTab && activeDay === 4 ? (
          <div className="themeupdate-card">
            <div className="themeupdate-card-header">Stage 4 — Word Chain</div>
            <div className="themeupdate-card-body">
              <div className="themeupdate-badge-upload-box" style={{ marginBottom: 20 }}>
                <label className="themeupdate-label">Logo</label>
                <input
                  type="file"
                  accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                  onChange={handleImageChange(setLogoFile, setLogoPreview, {
                    ...LOGO_DIMENSIONS,
                    label: "Logo",
                    allowJpeg: true,
                  })}
                />
                <div className="themeupdate-help">
                  <span>* PNG or JPEG, {LOGO_DIMENSIONS.width}×{LOGO_DIMENSIONS.height} px</span>
                </div>
                {(logoPreview || savedLogoUrl) && (
                  <div style={{ marginTop: 10 }}>
                    <div className="themeupdate-help" style={{ color: "#6c757d" }}>
                      <span>{logoPreview ? "Selected image" : "Saved image"}</span>
                    </div>
                    <img
                      src={logoPreview || savedLogoUrl}
                      alt="Stage 4 logo"
                      className="themeupdate-preview-img"
                    />
                  </div>
                )}
              </div>

              <div className="themeupdate-total-questions" style={{ marginBottom: 18, borderTop: "none", paddingTop: 0 }}>
                <label className="themeupdate-label">
                  Title
                  <span style={{ fontWeight: 400, fontSize: 13, color: "#6c757d", marginLeft: 8 }}>
                    (Max {TITLE_MAX} chars)
                  </span>
                </label>
                <input
                  type="text"
                  className="themeupdate-input"
                  value={stage.title ?? ""}
                  maxLength={TITLE_MAX}
                  onChange={(e) => {
                    const val = clampWithWarn(e.target.value, TITLE_MAX, "Title");
                    updateStage({ title: val });
                  }}
                  placeholder="Stage title"
                />
                <CharCount value={stage.title} max={TITLE_MAX} />
              </div>

              <div className="themeupdate-rules-section">
                <label className="themeupdate-label">Rules</label>
                <div className="themeupdate-rules-repeater">
                  {(Array.isArray(stage.rules) ? stage.rules : [""]).map((r, idx) => (
                    <div key={idx}>
                      <div className="themeupdate-rule-row">
                        <input
                          type="text"
                          className="themeupdate-input themeupdate-rule-input"
                          value={r ?? ""}
                          onChange={(e) => updateRuleCell(idx, e.target.value)}
                          placeholder={`Rule ${idx + 1}`}
                          maxLength={200}
                        />
                        <button
                          type="button"
                          className="themeupdate-btn themeupdate-btn-sm themeupdate-btn-danger"
                          onClick={() => removeRule(idx)}
                          disabled={(stage.rules?.length ?? 1) <= 1}
                          aria-label="Remove rule"
                        >
                          −
                        </button>
                      </div>
                      <CharCount value={r} max={200} />
                    </div>
                  ))}
                  <button
                    type="button"
                    className="themeupdate-btn themeupdate-btn-sm themeupdate-btn-primary themeupdate-add-rule"
                    onClick={addRule}
                    disabled={(stage.rules?.length ?? 1) >= 6}
                  >
                    + Add rule
                  </button>
                  <div className="themeupdate-help" style={{ marginTop: 6 }}>
                    <span>Max 6 rules. At least 1 required. 200 characters per rule.</span>
                  </div>
                </div>
              </div>

              <StageTimerInput stage={stage} updateStage={updateStage} />

              <div className="themeupdate-total-questions">
                <label className="themeupdate-label">
                  Description
                  <span style={{ fontWeight: 400, fontSize: 13, color: "#6c757d", marginLeft: 8 }}>
                    (Max {DESCRIPTION_MAX} chars)
                  </span>
                </label>
                <textarea
                  className="themeupdate-input themeupdate-textarea"
                  value={descriptionToTextareaMultiline(stage.description)}
                  maxLength={DESCRIPTION_MAX}
                  rows={6}
                  onChange={(e) => {
                    const val = clampWithWarn(e.target.value, DESCRIPTION_MAX, "Description");
                    updateStage({ description: descriptionFromTextareaMultiline(val) });
                  }}
                  placeholder={`e.g. ${STAGE_DESCRIPTION_EXAMPLES[4]}`}
                />
                <CharCount value={descriptionToTextareaMultiline(stage.description)} max={DESCRIPTION_MAX} />
              </div>

              <div className="themeupdate-actions" style={{ justifyContent: "center", marginTop: 28 }}>
                <button
                  type="button"
                  className="themeupdate-btn themeupdate-btn-success"
                  onClick={handleSaveDay4All}
                  disabled={savingDay}
                >
                  {savingDay ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        ) : !activePuzzleTab && activeDay === 1 ? (
          <div className="themeupdate-card">
            <div className="themeupdate-card-header">Stage 1 — Super Power Squad</div>
            <div className="themeupdate-card-body">
              <div className="themeupdate-badge-upload-box" style={{ marginBottom: 20 }}>
                <label className="themeupdate-label">Logo</label>
                <input
                  type="file"
                  accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                  onChange={handleImageChange(setLogoFile, setLogoPreview, {
                    ...LOGO_DIMENSIONS,
                    label: "Logo",
                    allowJpeg: true,
                  })}
                />
                <div className="themeupdate-help">
                  <span>* PNG or JPEG, {LOGO_DIMENSIONS.width}×{LOGO_DIMENSIONS.height} px</span>
                </div>
                {(logoPreview || savedLogoUrl) && (
                  <div style={{ marginTop: 10 }}>
                    <div className="themeupdate-help" style={{ color: "#6c757d" }}>
                      <span>{logoPreview ? "Selected image" : "Saved image"}</span>
                    </div>
                    <img
                      src={logoPreview || savedLogoUrl}
                      alt="Stage 1 logo"
                      className="themeupdate-preview-img"
                    />
                  </div>
                )}
              </div>

              <div className="themeupdate-total-questions" style={{ marginBottom: 18, borderTop: "none", paddingTop: 0 }}>
                <label className="themeupdate-label">
                  Title
                  <span style={{ fontWeight: 400, fontSize: 13, color: "#6c757d", marginLeft: 8 }}>
                    (Max {TITLE_MAX} chars)
                  </span>
                </label>
                <input
                  type="text"
                  className="themeupdate-input"
                  value={stage.title ?? ""}
                  maxLength={TITLE_MAX}
                  onChange={(e) => {
                    const val = clampWithWarn(e.target.value, TITLE_MAX, "Title");
                    updateStage({ title: val });
                  }}
                  placeholder="Stage title"
                />
                <CharCount value={stage.title} max={TITLE_MAX} />
              </div>

              <div className="themeupdate-rules-section">
                <label className="themeupdate-label">Rules</label>
                <div className="themeupdate-rules-repeater">
                  {(Array.isArray(stage.rules) ? stage.rules : [""]).map((r, idx) => (
                    <div key={idx}>
                      <div className="themeupdate-rule-row">
                        <input
                          type="text"
                          className="themeupdate-input themeupdate-rule-input"
                          value={r ?? ""}
                          onChange={(e) => updateRuleCell(idx, e.target.value)}
                          placeholder={`Rule ${idx + 1}`}
                          maxLength={200}
                        />
                        <button
                          type="button"
                          className="themeupdate-btn themeupdate-btn-sm themeupdate-btn-danger"
                          onClick={() => removeRule(idx)}
                          disabled={(stage.rules?.length ?? 1) <= 1}
                          aria-label="Remove rule"
                        >
                          −
                        </button>
                      </div>
                      <CharCount value={r} max={200} />
                    </div>
                  ))}
                  <button
                    type="button"
                    className="themeupdate-btn themeupdate-btn-sm themeupdate-btn-primary themeupdate-add-rule"
                    onClick={addRule}
                    disabled={(stage.rules?.length ?? 1) >= 6}
                  >
                    + Add rule
                  </button>
                  <div className="themeupdate-help" style={{ marginTop: 6 }}>
                    <span>Max 6 rules. At least 1 required. 200 characters per rule.</span>
                  </div>
                </div>
              </div>

              <StageTimerInput stage={stage} updateStage={updateStage} />

              <div className="themeupdate-total-questions">
                <label className="themeupdate-label">
                  Description
                  <span style={{ fontWeight: 400, fontSize: 13, color: "#6c757d", marginLeft: 8 }}>
                    (Max {getDayDescriptionMax(1)} chars)
                  </span>
                </label>
                <textarea
                  className="themeupdate-input themeupdate-textarea"
                  value={descriptionToTextareaMultiline(stage.description)}
                  maxLength={getDayDescriptionMax(1)}
                  rows={6}
                  onChange={(e) => {
                    const val = clampWithWarn(e.target.value, getDayDescriptionMax(1), "Description");
                    updateStage({ description: descriptionFromTextareaMultiline(val) });
                  }}
                  placeholder={`e.g. ${STAGE_DESCRIPTION_EXAMPLES[1]}`}
                />
                <CharCount value={descriptionToTextareaMultiline(stage.description)} max={getDayDescriptionMax(1)} />
              </div>

              <div className="themeupdate-actions" style={{ justifyContent: "center", marginTop: 28 }}>
                <button
                  type="button"
                  className="themeupdate-btn themeupdate-btn-success"
                  onClick={handleSaveDay1All}
                  disabled={savingDay}
                >
                  {savingDay ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        ) : !activePuzzleTab && activeDay === 6 ? (
          <div className="themeupdate-card">
            <div className="themeupdate-card-header">Stage 6 — Team Anthem</div>
            <div className="themeupdate-card-body">
              <div className="themeupdate-badge-upload-box" style={{ marginBottom: 20 }}>
                <label className="themeupdate-label">Logo</label>
                <input
                  type="file"
                  accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                  onChange={handleImageChange(setLogoFile, setLogoPreview, {
                    ...LOGO_DIMENSIONS,
                    label: "Logo",
                    allowJpeg: true,
                  })}
                />
                <div className="themeupdate-help">
                  <span>* PNG or JPEG, {LOGO_DIMENSIONS.width}×{LOGO_DIMENSIONS.height} px</span>
                </div>
                {(logoPreview || savedLogoUrl) && (
                  <div style={{ marginTop: 10 }}>
                    <div className="themeupdate-help" style={{ color: "#6c757d" }}>
                      <span>{logoPreview ? "Selected image" : "Saved image"}</span>
                    </div>
                    <img
                      src={logoPreview || savedLogoUrl}
                      alt="Stage 6 logo"
                      className="themeupdate-preview-img"
                    />
                  </div>
                )}
              </div>

              <div className="themeupdate-total-questions" style={{ marginBottom: 18, borderTop: "none", paddingTop: 0 }}>
                <label className="themeupdate-label">
                  Title
                  <span style={{ fontWeight: 400, fontSize: 13, color: "#6c757d", marginLeft: 8 }}>
                    (Max {TITLE_MAX} chars)
                  </span>
                </label>
                <input
                  type="text"
                  className="themeupdate-input"
                  value={stage.title ?? ""}
                  maxLength={TITLE_MAX}
                  onChange={(e) => {
                    const val = clampWithWarn(e.target.value, TITLE_MAX, "Title");
                    updateStage({ title: val });
                  }}
                  placeholder="Stage title"
                />
                <CharCount value={stage.title} max={TITLE_MAX} />
              </div>

              <div className="themeupdate-rules-section">
                <label className="themeupdate-label">Rules</label>
                <div className="themeupdate-rules-repeater">
                  {(Array.isArray(stage.rules) ? stage.rules : [""]).map((r, idx) => (
                    <div key={idx}>
                      <div className="themeupdate-rule-row">
                        <input
                          type="text"
                          className="themeupdate-input themeupdate-rule-input"
                          value={r ?? ""}
                          onChange={(e) => updateRuleCell(idx, e.target.value)}
                          placeholder={`Rule ${idx + 1}`}
                          maxLength={200}
                        />
                        <button
                          type="button"
                          className="themeupdate-btn themeupdate-btn-sm themeupdate-btn-danger"
                          onClick={() => removeRule(idx)}
                          disabled={(stage.rules?.length ?? 1) <= 1}
                          aria-label="Remove rule"
                        >
                          −
                        </button>
                      </div>
                      <CharCount value={r} max={200} />
                    </div>
                  ))}
                  <button
                    type="button"
                    className="themeupdate-btn themeupdate-btn-sm themeupdate-btn-primary themeupdate-add-rule"
                    onClick={addRule}
                    disabled={(stage.rules?.length ?? 1) >= 6}
                  >
                    + Add rule
                  </button>
                  <div className="themeupdate-help" style={{ marginTop: 6 }}>
                    <span>Max 6 rules. At least 1 required. 200 characters per rule.</span>
                  </div>
                </div>
              </div>

              <StageTimerInput stage={stage} updateStage={updateStage} />

              <div className="themeupdate-total-questions">
                <label className="themeupdate-label">
                  Description
                  <span style={{ fontWeight: 400, fontSize: 13, color: "#6c757d", marginLeft: 8 }}>
                    (Max {getDayDescriptionMax(6)} chars)
                  </span>
                </label>
                <textarea
                  className="themeupdate-input themeupdate-textarea"
                  value={descriptionToTextarea(stage.description)}
                  maxLength={getDayDescriptionMax(6)}
                  rows={4}
                  onChange={(e) => {
                    const val = clampWithWarn(e.target.value, getDayDescriptionMax(6), "Description");
                    updateStage({ description: descriptionFromTextarea(val) });
                  }}
                  placeholder={`e.g. ${STAGE_DESCRIPTION_EXAMPLES[6]}`}
                />
                <CharCount value={descriptionToTextarea(stage.description)} max={getDayDescriptionMax(6)} />
              </div>

              <div className="themeupdate-actions" style={{ justifyContent: "center", marginTop: 28 }}>
                <button
                  type="button"
                  className="themeupdate-btn themeupdate-btn-success"
                  onClick={handleSaveDay6All}
                  disabled={savingDay}
                >
                  {savingDay ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        ) : !activePuzzleTab && activeDay === 7 ? (
          <div className="themeupdate-card">
            <div className="themeupdate-card-header">Stage 7 — Super Squad</div>
            <div className="themeupdate-card-body">
              <div className="themeupdate-badge-upload-box" style={{ marginBottom: 20 }}>
                <label className="themeupdate-label">Logo</label>
                <input
                  type="file"
                  accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                  onChange={handleImageChange(setLogoFile, setLogoPreview, {
                    ...LOGO_DIMENSIONS,
                    label: "Logo",
                    allowJpeg: true,
                  })}
                />
                <div className="themeupdate-help">
                  <span>* PNG or JPEG, {LOGO_DIMENSIONS.width}×{LOGO_DIMENSIONS.height} px</span>
                </div>
                {(logoPreview || savedLogoUrl) && (
                  <div style={{ marginTop: 10 }}>
                    <div className="themeupdate-help" style={{ color: "#6c757d" }}>
                      <span>{logoPreview ? "Selected image" : "Saved image"}</span>
                    </div>
                    <img
                      src={logoPreview || savedLogoUrl}
                      alt="Stage 7 logo"
                      className="themeupdate-preview-img"
                    />
                  </div>
                )}
              </div>

              <div className="themeupdate-total-questions" style={{ marginBottom: 18, borderTop: "none", paddingTop: 0 }}>
                <label className="themeupdate-label">
                  Title
                  <span style={{ fontWeight: 400, fontSize: 13, color: "#6c757d", marginLeft: 8 }}>
                    (Max {TITLE_MAX} chars)
                  </span>
                </label>
                <input
                  type="text"
                  className="themeupdate-input"
                  value={stage.title ?? ""}
                  maxLength={TITLE_MAX}
                  onChange={(e) => {
                    const val = clampWithWarn(e.target.value, TITLE_MAX, "Title");
                    updateStage({ title: val });
                  }}
                  placeholder="Stage title"
                />
                <CharCount value={stage.title} max={TITLE_MAX} />
              </div>

              <div className="themeupdate-rules-section">
                <label className="themeupdate-label">Rules</label>
                <div className="themeupdate-rules-repeater">
                  {(Array.isArray(stage.rules) ? stage.rules : [""]).map((r, idx) => (
                    <div key={idx}>
                      <div className="themeupdate-rule-row">
                        <input
                          type="text"
                          className="themeupdate-input themeupdate-rule-input"
                          value={r ?? ""}
                          onChange={(e) => updateRuleCell(idx, e.target.value)}
                          placeholder={`Rule ${idx + 1}`}
                          maxLength={200}
                        />
                        <button
                          type="button"
                          className="themeupdate-btn themeupdate-btn-sm themeupdate-btn-danger"
                          onClick={() => removeRule(idx)}
                          disabled={(stage.rules?.length ?? 1) <= 1}
                          aria-label="Remove rule"
                        >
                          −
                        </button>
                      </div>
                      <CharCount value={r} max={200} />
                    </div>
                  ))}
                  <button
                    type="button"
                    className="themeupdate-btn themeupdate-btn-sm themeupdate-btn-primary themeupdate-add-rule"
                    onClick={addRule}
                    disabled={(stage.rules?.length ?? 1) >= 6}
                  >
                    + Add rule
                  </button>
                  <div className="themeupdate-help" style={{ marginTop: 6 }}>
                    <span>Max 6 rules. At least 1 required. 200 characters per rule.</span>
                  </div>
                </div>
              </div>

              <StageTimerInput stage={stage} updateStage={updateStage} />

              <div className="themeupdate-total-questions">
                <label className="themeupdate-label">
                  Description
                  <span style={{ fontWeight: 400, fontSize: 13, color: "#6c757d", marginLeft: 8 }}>
                    (Max {getDayDescriptionMax(7)} chars)
                  </span>
                </label>
                <textarea
                  className="themeupdate-input themeupdate-textarea"
                  value={descriptionToTextareaMultiline(stage.description)}
                  maxLength={getDayDescriptionMax(7)}
                  rows={6}
                  onChange={(e) => {
                    const val = clampWithWarn(e.target.value, getDayDescriptionMax(7), "Description");
                    updateStage({ description: descriptionFromTextareaMultiline(val) });
                  }}
                  placeholder={`e.g. ${STAGE_DESCRIPTION_EXAMPLES[7]}`}
                />
                <CharCount value={descriptionToTextareaMultiline(stage.description)} max={getDayDescriptionMax(7)} />
              </div>

              <div className="themeupdate-actions" style={{ justifyContent: "center", marginTop: 28 }}>
                <button
                  type="button"
                  className="themeupdate-btn themeupdate-btn-success"
                  onClick={handleSaveDay7All}
                  disabled={savingDay}
                >
                  {savingDay ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        ) : !activePuzzleTab && Number(activeDay) === 8 ? (
          <div className="themeupdate-card">
            <div className="themeupdate-card-header">Stage 8 — Wild Tales</div>
            <div className="themeupdate-card-body">
              <div className="themeupdate-badge-upload-box" style={{ marginBottom: 20 }}>
                <label className="themeupdate-label">Logo</label>
                <input
                  type="file"
                  accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                  onChange={handleImageChange(setLogoFile, setLogoPreview, {
                    ...LOGO_DIMENSIONS,
                    label: "Logo",
                    allowJpeg: true,
                  })}
                />
                <div className="themeupdate-help">
                  <span>* PNG or JPEG, {LOGO_DIMENSIONS.width}×{LOGO_DIMENSIONS.height} px</span>
                </div>
                {(logoPreview || savedLogoUrl) && (
                  <div style={{ marginTop: 10 }}>
                    <div className="themeupdate-help" style={{ color: "#6c757d" }}>
                      <span>{logoPreview ? "Selected image" : "Saved image"}</span>
                    </div>
                    <img
                      src={logoPreview || savedLogoUrl}
                      alt="Stage 8 logo"
                      className="themeupdate-preview-img"
                    />
                  </div>
                )}
              </div>

              <div className="themeupdate-total-questions" style={{ marginBottom: 18, borderTop: "none", paddingTop: 0 }}>
                <label className="themeupdate-label">
                  Title
                  <span style={{ fontWeight: 400, fontSize: 13, color: "#6c757d", marginLeft: 8 }}>
                    (Max {TITLE_MAX} chars)
                  </span>
                </label>
                <input
                  type="text"
                  className="themeupdate-input"
                  value={stage.title ?? ""}
                  maxLength={TITLE_MAX}
                  onChange={(e) => {
                    const val = clampWithWarn(e.target.value, TITLE_MAX, "Title");
                    updateStage({ title: val });
                  }}
                  placeholder="Stage title"
                />
                <CharCount value={stage.title} max={TITLE_MAX} />
              </div>

              <div className="themeupdate-rules-section">
                <label className="themeupdate-label">Rules</label>
                <div className="themeupdate-rules-repeater">
                  {(Array.isArray(stage.rules) ? stage.rules : [""]).map((r, idx) => (
                    <div key={idx}>
                      <div className="themeupdate-rule-row">
                        <input
                          type="text"
                          className="themeupdate-input themeupdate-rule-input"
                          value={r ?? ""}
                          onChange={(e) => updateRuleCell(idx, e.target.value)}
                          placeholder={`Rule ${idx + 1}`}
                          maxLength={200}
                        />
                        <button
                          type="button"
                          className="themeupdate-btn themeupdate-btn-sm themeupdate-btn-danger"
                          onClick={() => removeRule(idx)}
                          disabled={(stage.rules?.length ?? 1) <= 1}
                          aria-label="Remove rule"
                        >
                          −
                        </button>
                      </div>
                      <CharCount value={r} max={200} />
                    </div>
                  ))}
                  <button
                    type="button"
                    className="themeupdate-btn themeupdate-btn-sm themeupdate-btn-primary themeupdate-add-rule"
                    onClick={addRule}
                    disabled={(stage.rules?.length ?? 1) >= 6}
                  >
                    + Add rule
                  </button>
                  <div className="themeupdate-help" style={{ marginTop: 6 }}>
                    <span>Max 6 rules. At least 1 required. 200 characters per rule.</span>
                  </div>
                </div>
              </div>

              <StageTimerInput stage={stage} updateStage={updateStage} />

              <div className="themeupdate-total-questions">
                <label className="themeupdate-label">
                  Description
                  <span style={{ fontWeight: 400, fontSize: 13, color: "#6c757d", marginLeft: 8 }}>
                    (Max {getDayDescriptionMax(8)} chars)
                  </span>
                </label>
                <textarea
                  className="themeupdate-input themeupdate-textarea"
                  value={descriptionToTextareaMultiline(stage.description)}
                  maxLength={getDayDescriptionMax(8)}
                  rows={6}
                  onChange={(e) => {
                    const val = clampWithWarn(e.target.value, getDayDescriptionMax(8), "Description");
                    updateStage({ description: descriptionFromTextareaMultiline(val) });
                  }}
                  placeholder="Stage description shown to players (press Enter for a new line)"
                />
                <CharCount value={descriptionToTextareaMultiline(stage.description)} max={getDayDescriptionMax(8)} />
              </div>

              <div className="themeupdate-actions" style={{ justifyContent: "center", marginTop: 28 }}>
                <button
                  type="button"
                  className="themeupdate-btn themeupdate-btn-success"
                  onClick={handleSaveDay8All}
                  disabled={savingDay}
                >
                  {savingDay ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        ) : !activePuzzleTab && Number(activeDay) === 9 ? (
          <div className="themeupdate-card">
            <div className="themeupdate-card-header">Stage 9 — Letter Loot</div>
            <div className="themeupdate-card-body">
              <div className="themeupdate-badge-upload-box" style={{ marginBottom: 20 }}>
                <label className="themeupdate-label">Logo</label>
                <input
                  type="file"
                  accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                  onChange={handleImageChange(setLogoFile, setLogoPreview, {
                    ...LOGO_DIMENSIONS,
                    label: "Logo",
                    allowJpeg: true,
                  })}
                />
                <div className="themeupdate-help">
                  <span>* PNG or JPEG, {LOGO_DIMENSIONS.width}×{LOGO_DIMENSIONS.height} px</span>
                </div>
                {(logoPreview || savedLogoUrl) && (
                  <div style={{ marginTop: 10 }}>
                    <div className="themeupdate-help" style={{ color: "#6c757d" }}>
                      <span>{logoPreview ? "Selected image" : "Saved image"}</span>
                    </div>
                    <img
                      src={logoPreview || savedLogoUrl}
                      alt="Stage 9 logo"
                      className="themeupdate-preview-img"
                    />
                  </div>
                )}
              </div>

              <div className="themeupdate-total-questions" style={{ marginBottom: 18, borderTop: "none", paddingTop: 0 }}>
                <label className="themeupdate-label">
                  Title
                  <span style={{ fontWeight: 400, fontSize: 13, color: "#6c757d", marginLeft: 8 }}>
                    (Max {TITLE_MAX} chars)
                  </span>
                </label>
                <input
                  type="text"
                  className="themeupdate-input"
                  value={stage.title ?? ""}
                  maxLength={TITLE_MAX}
                  onChange={(e) => {
                    const val = clampWithWarn(e.target.value, TITLE_MAX, "Title");
                    updateStage({ title: val });
                  }}
                  placeholder="Stage title"
                />
                <CharCount value={stage.title} max={TITLE_MAX} />
              </div>

              <div className="themeupdate-rules-section">
                <label className="themeupdate-label">Rules</label>
                <div className="themeupdate-rules-repeater">
                  {(Array.isArray(stage.rules) ? stage.rules : [""]).map((r, idx) => (
                    <div key={idx}>
                      <div className="themeupdate-rule-row">
                        <input
                          type="text"
                          className="themeupdate-input themeupdate-rule-input"
                          value={r ?? ""}
                          onChange={(e) => updateRuleCell(idx, e.target.value)}
                          placeholder={`Rule ${idx + 1}`}
                          maxLength={200}
                        />
                        <button
                          type="button"
                          className="themeupdate-btn themeupdate-btn-sm themeupdate-btn-danger"
                          onClick={() => removeRule(idx)}
                          disabled={(stage.rules?.length ?? 1) <= 1}
                          aria-label="Remove rule"
                        >
                          −
                        </button>
                      </div>
                      <CharCount value={r} max={200} />
                    </div>
                  ))}
                  <button
                    type="button"
                    className="themeupdate-btn themeupdate-btn-sm themeupdate-btn-primary themeupdate-add-rule"
                    onClick={addRule}
                    disabled={(stage.rules?.length ?? 1) >= 6}
                  >
                    + Add rule
                  </button>
                  <div className="themeupdate-help" style={{ marginTop: 6 }}>
                    <span>Max 6 rules. At least 1 required. 200 characters per rule.</span>
                  </div>
                </div>
              </div>

              <StageTimerInput stage={stage} updateStage={updateStage} />

              <div className="themeupdate-total-questions">
                <label className="themeupdate-label">
                  Description
                  <span style={{ fontWeight: 400, fontSize: 13, color: "#6c757d", marginLeft: 8 }}>
                    (Max {getDayDescriptionMax(9)} chars)
                  </span>
                </label>
                <textarea
                  className="themeupdate-input themeupdate-textarea"
                  value={descriptionToTextareaMultiline(stage.description)}
                  maxLength={getDayDescriptionMax(9)}
                  rows={6}
                  onChange={(e) => {
                    const val = clampWithWarn(e.target.value, getDayDescriptionMax(9), "Description");
                    updateStage({ description: descriptionFromTextareaMultiline(val) });
                  }}
                  placeholder={`e.g. ${STAGE_DESCRIPTION_EXAMPLES[9]}`}
                />
                <CharCount value={descriptionToTextareaMultiline(stage.description)} max={getDayDescriptionMax(9)} />
              </div>

              <div className="themeupdate-actions" style={{ justifyContent: "center", marginTop: 28 }}>
                <button
                  type="button"
                  className="themeupdate-btn themeupdate-btn-success"
                  onClick={handleSaveDay9All}
                  disabled={savingDay}
                >
                  {savingDay ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        ) : !activePuzzleTab && Number(activeDay) === 10 ? (
          <div className="themeupdate-card">
            <div className="themeupdate-card-header">Stage 10 — Native Places</div>
            <div className="themeupdate-card-body">
              <div className="themeupdate-badge-upload-box" style={{ marginBottom: 20 }}>
                <label className="themeupdate-label">Logo</label>
                <input
                  type="file"
                  accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                  onChange={handleImageChange(setLogoFile, setLogoPreview, {
                    ...LOGO_DIMENSIONS,
                    label: "Logo",
                    allowJpeg: true,
                  })}
                />
                <div className="themeupdate-help">
                  <span>* PNG or JPEG, {LOGO_DIMENSIONS.width}×{LOGO_DIMENSIONS.height} px</span>
                </div>
                {(logoPreview || savedLogoUrl) && (
                  <div style={{ marginTop: 10 }}>
                    <div className="themeupdate-help" style={{ color: "#6c757d" }}>
                      <span>{logoPreview ? "Selected image" : "Saved image"}</span>
                    </div>
                    <img
                      src={logoPreview || savedLogoUrl}
                      alt="Stage 10 logo"
                      className="themeupdate-preview-img"
                    />
                  </div>
                )}
              </div>

              <div className="themeupdate-total-questions" style={{ marginBottom: 18, borderTop: "none", paddingTop: 0 }}>
                <label className="themeupdate-label">
                  Title
                  <span style={{ fontWeight: 400, fontSize: 13, color: "#6c757d", marginLeft: 8 }}>
                    (Max {TITLE_MAX} chars)
                  </span>
                </label>
                <input
                  type="text"
                  className="themeupdate-input"
                  value={stage.title ?? ""}
                  maxLength={TITLE_MAX}
                  onChange={(e) => {
                    const val = clampWithWarn(e.target.value, TITLE_MAX, "Title");
                    updateStage({ title: val });
                  }}
                  placeholder="Stage title"
                />
                <CharCount value={stage.title} max={TITLE_MAX} />
              </div>

              <div className="themeupdate-rules-section">
                <label className="themeupdate-label">Rules</label>
                <div className="themeupdate-rules-repeater">
                  {(Array.isArray(stage.rules) ? stage.rules : [""]).map((r, idx) => (
                    <div key={idx}>
                      <div className="themeupdate-rule-row">
                        <input
                          type="text"
                          className="themeupdate-input themeupdate-rule-input"
                          value={r ?? ""}
                          onChange={(e) => updateRuleCell(idx, e.target.value)}
                          placeholder={`Rule ${idx + 1}`}
                          maxLength={200}
                        />
                        <button
                          type="button"
                          className="themeupdate-btn themeupdate-btn-sm themeupdate-btn-danger"
                          onClick={() => removeRule(idx)}
                          disabled={(stage.rules?.length ?? 1) <= 1}
                          aria-label="Remove rule"
                        >
                          −
                        </button>
                      </div>
                      <CharCount value={r} max={200} />
                    </div>
                  ))}
                  <button
                    type="button"
                    className="themeupdate-btn themeupdate-btn-sm themeupdate-btn-primary themeupdate-add-rule"
                    onClick={addRule}
                    disabled={(stage.rules?.length ?? 1) >= 6}
                  >
                    + Add rule
                  </button>
                  <div className="themeupdate-help" style={{ marginTop: 6 }}>
                    <span>Max 6 rules. At least 1 required. 200 characters per rule.</span>
                  </div>
                </div>
              </div>

              <StageTimerInput stage={stage} updateStage={updateStage} />

              <div className="themeupdate-total-questions">
                <label className="themeupdate-label">
                  Description
                  <span style={{ fontWeight: 400, fontSize: 13, color: "#6c757d", marginLeft: 8 }}>
                    (Max {getDayDescriptionMax(10)} chars)
                  </span>
                </label>
                <textarea
                  className="themeupdate-input themeupdate-textarea"
                  value={descriptionToTextareaMultiline(stage.description)}
                  maxLength={getDayDescriptionMax(10)}
                  rows={6}
                  onChange={(e) => {
                    const val = clampWithWarn(e.target.value, getDayDescriptionMax(10), "Description");
                    updateStage({ description: descriptionFromTextareaMultiline(val) });
                  }}
                  placeholder={`e.g. ${STAGE_DESCRIPTION_EXAMPLES[10]}`}
                />
                <CharCount value={descriptionToTextareaMultiline(stage.description)} max={getDayDescriptionMax(10)} />
              </div>

              <div className="themeupdate-actions" style={{ justifyContent: "center", marginTop: 28 }}>
                <button
                  type="button"
                  className="themeupdate-btn themeupdate-btn-success"
                  onClick={handleSaveDay10All}
                  disabled={savingDay}
                >
                  {savingDay ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {activePuzzleTab ? (
          <div className="themeupdate-card">
            <div className="themeupdate-card-header">
              Puzzle Settings
              {savedTotalPuzzles != null ? (
                <span style={{ fontWeight: 400, fontSize: 14, marginLeft: 12, color: "#6c757d" }}>
                  (total_puzzles: {savedTotalPuzzles})
                </span>
              ) : null}
            </div>
            <div className="themeupdate-card-body">
              <div className="themeupdate-badge-upload-box" style={{ marginBottom: 24 }}>
                <label className="themeupdate-label">Main Big Picture Image</label>
                <input
                  ref={puzzleMainInputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                  onChange={handlePuzzleMainChange}
                />
                <div className="themeupdate-help">
                  <span>
                    * PNG or JPEG, {PUZZLE_IMAGE_DIMENSIONS.width}×{PUZZLE_IMAGE_DIMENSIONS.height} px, max{" "}
                    {PUZZLE_IMAGE_MAX_BYTES / (1024 * 1024)} MB. This is the complete puzzle image shown as preview.
                  </span>
                </div>
                {savedPuzzleMainLoading ? (
                  <div className="themeupdate-help" style={{ marginTop: 10, color: "#6c757d" }}>
                    Loading saved puzzle image…
                  </div>
                ) : null}
                {savedPuzzleMainError && !puzzleMainPreview && !puzzleMainFile ? (
                  <div className="themeupdate-help" style={{ marginTop: 10, color: "#c62828" }}>
                    {savedPuzzleMainError}
                  </div>
                ) : null}
                {resolvedPuzzleMainSrc ? (
                  <div style={{ marginTop: 10 }}>
                    <div className="themeupdate-help" style={{ color: "#6c757d" }}>
                      <span>{puzzleMainPreview ? "Selected image" : "Saved image"}</span>
                    </div>
                    <img
                      src={puzzleMainDisplaySrc}
                      alt="Main puzzle"
                      className="themeupdate-preview-img"
                      style={{ maxWidth: 300, maxHeight: 300 }}
                      onError={(e) => {
                        if (
                          savedPuzzleMainObjectUrl &&
                          e.currentTarget.src !== savedPuzzleMainObjectUrl
                        ) {
                          e.currentTarget.src = savedPuzzleMainObjectUrl;
                        }
                      }}
                    />
                  </div>
                ) : savedPuzzleImageFilename ? (
                  <div className="themeupdate-help" style={{ marginTop: 10, color: "#6c757d" }}>
                    Saved puzzle image on file ({savedPuzzleImageFilename}). You can change the piece count below without re-uploading.
                  </div>
                ) : null}
              </div>

              <div style={{ marginBottom: 24 }}>
                <label className="themeupdate-label">Total Puzzles</label>
                <div className="themeupdate-help" style={{ marginBottom: 8 }}>
                  <span>
                    Enter how many pieces to split the full Big Picture into ({PUZZLE_PARTS_MIN}–{PUZZLE_PARTS_MAX}).
                    The whole image is used with no extra or missing parts.
                    {savedPuzzleImageFilename
                      ? " Change the count and click Save — the saved image will be re-cut automatically."
                      : ""}
                    {savedTotalPuzzles != null ? ` Saved value: ${savedTotalPuzzles}.` : ""}
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                  <input
                    type="number"
                    className="themeupdate-input"
                    min={PUZZLE_PARTS_MIN}
                    max={PUZZLE_PARTS_MAX}
                    value={puzzlePartsInput}
                    onChange={(e) => {
                      const raw = e.target.value;
                      setPuzzlePartsInput(raw);
                      if (raw === "") return;
                      const val = Math.floor(Number(raw));
                      if (Number.isFinite(val) && val >= PUZZLE_PARTS_MIN && val <= PUZZLE_PARTS_MAX) {
                        setPuzzleParts(val);
                      }
                    }}
                    onBlur={() => {
                      const val = clampPuzzleParts(puzzlePartsInput, puzzleParts);
                      if (
                        puzzlePartsInput !== "" &&
                        Number.isFinite(Number(puzzlePartsInput)) &&
                        (Math.floor(Number(puzzlePartsInput)) < PUZZLE_PARTS_MIN ||
                          Math.floor(Number(puzzlePartsInput)) > PUZZLE_PARTS_MAX)
                      ) {
                        void Swal.fire({
                          icon: "warning",
                          title: "Invalid puzzle count",
                          text: `Total puzzles must be between ${PUZZLE_PARTS_MIN} and ${PUZZLE_PARTS_MAX}.`,
                        });
                      }
                      setPuzzleParts(val);
                      setPuzzlePartsInput(String(val));
                    }}
                    style={{ width: 100 }}
                  />
                  <span style={{ fontSize: 14, color: "#6c757d" }}>pieces</span>
                </div>
                <PuzzlePartsPreview
                  imageSrc={resolvedPuzzleMainSrc}
                  parts={puzzleParts}
                  useSavedPieces={useSavedPuzzlePieces}
                  assetFilename={savedPuzzleImageFilename}
                  adminToken={adminToken}
                  savedPieceUrls={
                    puzzlePiecePreviews.length > 0 && puzzlePiecePreviewParts === puzzleParts
                      ? puzzlePiecePreviews
                      : savedPuzzlePartUrls
                  }
                />
              </div>

              <div className="themeupdate-actions" style={{ justifyContent: "center", marginTop: 28 }}>
                <button
                  type="button"
                  className="themeupdate-btn themeupdate-btn-success"
                  onClick={handleSavePuzzle}
                  disabled={savingPuzzle}
                >
                  {savingPuzzle ? "Saving…" : "Save Puzzle Settings"}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
